/**
 * LYT Communications - Shared Map Page (Public / No Auth)
 *
 * Project map for GC (general contractor) and contractor visibility.
 * URL: lytcomm.com/#shared-map?project=BASTROP
 *
 * No login required. Contractors can add/edit/move their own pins.
 * Colored segments, device markers, progress stats, section filter.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBS, STATUS_COLORS, DEFAULT_MAP_PRESETS } from '../config/mapConfig';
import { loadProjects, loadSegments, loadDevices, loadSections, loadMapPresets, loadMapNotes, transitionMapNote, createNoteReply, loadNoteReplies, createMapNote, editMapNote, moveMapNote, uploadNotePhoto, updateItemStatus } from '../services/mapService';
import { uploadPhoto } from '../services/photoUploadService';
import { Sun, Moon, ChevronDown, BarChart3, Layers, RefreshCw, Eye, Settings, Maximize2, Minimize2, LocateFixed, WifiOff, MapPin, Plus, Download } from 'lucide-react';
import { getTheme } from '../utils/theme';
import compressImage from '../utils/compressImage';
import { getPreset } from '../config/pinPresets';
import { enqueueAction, flushQueue, getQueueCount, cacheNotes, getCachedNotes } from '../services/offlineQueueService';
import useOnlineStatus from '../hooks/useOnlineStatus';

// --- Helpers ---

function normalizePhone(p) {
  return (p || '').replace(/\D/g, '');
}

// Pin category colors & letters — each category is visually distinct on the map
const PIN_CATEGORIES = [
  { key: 'general', label: 'General', letter: 'G', color: '#1565C0' },
  { key: 'issue', label: 'Issue', letter: '!', color: '#D32F2F' },
  { key: 'safety', label: 'Safety', letter: 'S', color: '#E65100' },
  { key: 'question', label: 'Question', letter: '?', color: '#7B1FA2' },
  { key: 'billing', label: 'Billing', letter: 'B', color: '#2E7D32' },
];
const PIN_CAT_MAP = Object.fromEntries(PIN_CATEGORIES.map(c => [c.key, c]));

function getStatusColor(status) {
  const map = {
    'Not Started': STATUS_COLORS.NOT_STARTED,
    'Potholing': STATUS_COLORS.IN_PROGRESS,
    'Pothole Approved': STATUS_COLORS.CRITICAL_PATH,
    'In Progress': STATUS_COLORS.IN_PROGRESS,
    'Complete': STATUS_COLORS.COMPLETE,
    'QC Approved': STATUS_COLORS.QC_APPROVED,
    'Blocked': STATUS_COLORS.BLOCKED,
    'Issue': STATUS_COLORS.ISSUE,
  };
  return map[status] || STATUS_COLORS.NOT_STARTED;
}

function buildDeviceSvg(type, darkMode) {
  // Each device type gets a unique shape AND color so they're distinguishable at a glance
  const s = 24;
  const mk = (svg) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}' viewBox='0 0 24 24'>${svg}</svg>`)}`;

  if (type === 'handhole') {
    // Blue square with rounded corners + "H" — distinct from flowerpot's circle
    const bg = darkMode ? '#42a5f5' : '#1565C0';
    return mk(
      `<rect x='3' y='3' width='18' height='18' rx='3' fill='${bg}' stroke='white' stroke-width='2'/>` +
      `<text x='12' y='16.5' text-anchor='middle' font-size='12' font-weight='bold' font-family='Arial,sans-serif' fill='white'>H</text>`
    );
  }
  if (type === 'flowerpot') {
    // Orange/amber circle with "F" — clearly different shape and color from handhole
    const bg = darkMode ? '#ffa726' : '#E65100';
    return mk(
      `<circle cx='12' cy='12' r='10' fill='${bg}' stroke='white' stroke-width='2'/>` +
      `<text x='12' y='16.5' text-anchor='middle' font-size='12' font-weight='bold' font-family='Arial,sans-serif' fill='white'>F</text>`
    );
  }
  if (type === 'splice_point' || type === 'splitter_1x4' || type === 'splitter_2x8') {
    // Red bowtie / hourglass (matches design legend: splice point)
    const bg = darkMode ? '#ef5350' : '#D32F2F';
    return mk(
      `<polygon points='2,4 12,12 2,20' fill='${bg}' stroke='white' stroke-width='1.5' stroke-linejoin='round'/>` +
      `<polygon points='22,4 12,12 22,20' fill='${bg}' stroke='white' stroke-width='1.5' stroke-linejoin='round'/>`
    );
  }
  if (type === 'pedestal' || type === 'cabinet') {
    // Green tall rectangle (pedestal/cabinet enclosure)
    const bg = darkMode ? '#66bb6a' : '#2E7D32';
    return mk(
      `<rect x='5' y='2' width='14' height='20' rx='2' fill='${bg}' stroke='white' stroke-width='1.5'/>` +
      `<line x1='12' y1='5' x2='12' y2='19' stroke='white' stroke-width='1' stroke-opacity='0.5'/>` +
      `<line x1='8' y1='6' x2='10' y2='6' stroke='white' stroke-width='1.3' stroke-linecap='round'/>` +
      `<line x1='8' y1='8.5' x2='10' y2='8.5' stroke='white' stroke-width='1.3' stroke-linecap='round'/>` +
      `<line x1='8' y1='11' x2='10' y2='11' stroke='white' stroke-width='1.3' stroke-linecap='round'/>`
    );
  }
  if (type === 'fat') {
    // Purple diamond shape with dot — distinct from splice bowtie
    const bg = darkMode ? '#ab47bc' : '#6A1B9A';
    return mk(
      `<polygon points='12,2 22,12 12,22 2,12' fill='${bg}' stroke='white' stroke-width='1.5'/>` +
      `<circle cx='12' cy='12' r='3' fill='white' fill-opacity='0.9'/>`
    );
  }
  // default
  return mk(`<circle cx='12' cy='12' r='8' fill='#78909c' stroke='white' stroke-width='1.5'/>`);
}

/**
 * Nudge overlapping markers so they fan out in a small ring.
 * Groups items by rounded GPS (5 decimal places ≈ 1m), then offsets each
 * item in a circle around the shared point.
 */
function spreadOverlapping(items, latKey, lngKey) {
  const PRECISION = 5; // ~1 meter grouping radius
  const OFFSET = 0.00004; // ~4 meter spread radius
  const groups = {};
  items.forEach((item, idx) => {
    const lat = parseFloat(item[latKey]);
    const lng = parseFloat(item[lngKey]);
    if (isNaN(lat) || isNaN(lng)) return;
    const key = `${lat.toFixed(PRECISION)},${lng.toFixed(PRECISION)}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(idx);
  });
  const offsets = new Array(items.length).fill(null);
  Object.values(groups).forEach(indices => {
    if (indices.length <= 1) {
      offsets[indices[0]] = { dlat: 0, dlng: 0 };
      return;
    }
    const n = indices.length;
    indices.forEach((idx, i) => {
      const angle = (2 * Math.PI * i) / n;
      offsets[idx] = {
        dlat: OFFSET * Math.cos(angle),
        dlng: OFFSET * Math.sin(angle),
      };
    });
  });
  return offsets;
}

function pct(n, d) {
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

// --- Map styles ---
const hidePoiStyle = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const darkMapStyle = [
  ...hidePoiStyle,
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
];

// --- localStorage helpers (separate keys per device type) ---
const SHARED_MAP_KEY_DESKTOP = 'lyt-shared-map-desktop';
const SHARED_MAP_KEY_MOBILE = 'lyt-shared-map-mobile';
const SHARED_MAP_KEY_LEGACY = 'lyt-shared-map-state';

function loadSaved(isMobile) {
  const key = isMobile ? SHARED_MAP_KEY_MOBILE : SHARED_MAP_KEY_DESKTOP;
  try {
    const data = JSON.parse(localStorage.getItem(key));
    if (data) return data;
    // Fall back to legacy key for one-time migration
    return JSON.parse(localStorage.getItem(SHARED_MAP_KEY_LEGACY)) || {};
  } catch { return {}; }
}

// --- Unread reply tracking ---
const SEEN_KEY = 'lyt-note-seen';
function getSeenCounts() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || {}; } catch { return {}; }
}
function markNoteSeen(noteId, replyCount) {
  const seen = getSeenCounts();
  seen[noteId] = replyCount;
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch {}
}
function hasUnread(note) {
  const count = parseInt(note.reply_count, 10) || 0;
  if (count === 0) return false;
  const seen = getSeenCounts();
  return count > (seen[note.note_id] || 0);
}

// --- Mobile detection ---
function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

// --- Keyboard-aware bottom offset (visualViewport API) ---
function useKeyboardHeight() {
  const [kbHeight, setKbHeight] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const offset = window.innerHeight - vv.height;
      setKbHeight(offset > 50 ? offset : 0); // threshold to ignore toolbar-only changes
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, []);
  return kbHeight;
}

// Visually hidden but accessible — fixes iOS Safari not opening file picker on display:none inputs
const srOnly = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 };

// --- Component ---

export default function SharedMapPage() {
  const isMobile = useIsMobile();
  const _saved = loadSaved(isMobile);

  // State (restored from localStorage where available)
  const [darkMode, setDarkMode] = useState(_saved.darkMode || false);
  const [projectId, setProjectId] = useState(null);
  const [project, setProject] = useState(null);
  const [segments, setSegments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(_saved.selectedSection || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLegend, setShowLegend] = useState(_saved.showLegend || false);
  const [showStats, setShowStats] = useState(_saved.showStats !== false);
  const [phase, setPhase] = useState(_saved.phase || 'boring');
  const [hoveredDevice, setHoveredDevice] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showHandholes, setShowHandholes] = useState(_saved.showHandholes !== false);
  const [showFlowerpots, setShowFlowerpots] = useState(_saved.showFlowerpots !== false);
  const [showSplicePoints, setShowSplicePoints] = useState(_saved.showSplicePoints !== false);
  const [showPedestals, setShowPedestals] = useState(_saved.showPedestals !== false);
  const [showCabinets, setShowCabinets] = useState(_saved.showCabinets !== false);
  const [showFats, setShowFats] = useState(_saved.showFats === true);
  const [showAerial, setShowAerial] = useState(_saved.showAerial || false);
  const [lytOnly, setLytOnly] = useState(_saved.lytOnly !== false);
  const [showNotes, setShowNotes] = useState(_saved.showNotes !== false);
  const [mapNotes, setMapNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteReplies, setNoteReplies] = useState([]);
  const [commentName, setCommentName] = useState(() => { try { return localStorage.getItem('lyt-commenter-name') || ''; } catch { return ''; } });
  const [commentPhone, setCommentPhone] = useState(() => { try { return localStorage.getItem('lyt-commenter-phone') || ''; } catch { return ''; } });
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [identityError, setIdentityError] = useState(false);
  const [presets, setPresets] = useState(DEFAULT_MAP_PRESETS);
  const [activePreset, setActivePreset] = useState(_saved.activePreset || 'construction');
  const [showSettings, setShowSettings] = useState(false);
  const [mapType, setMapType] = useState(_saved.mapType || 'hybrid');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [gpsFollowing, setGpsFollowing] = useState(false);
  const { isOnline, pingApi } = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(() => getQueueCount());
  const [syncToast, setSyncToast] = useState(null); // { type: 'amber'|'green', message }
  // Pin creation / edit / move state
  const [placingPin, setPlacingPin] = useState(false);
  const [newPinLocation, setNewPinLocation] = useState(null); // { lat, lng }
  const [pinFormText, setPinFormText] = useState('');
  const [pinFormCategory, setPinFormCategory] = useState('general');
  const [pinFormSaving, setPinFormSaving] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // note being edited
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('general');
  const [editSaving, setEditSaving] = useState(false);
  const [movingNote, setMovingNote] = useState(null); // note being moved
  // Photo state
  const [pendingPhotos, setPendingPhotos] = useState([]); // File objects for new pin
  const [photoUploading, setPhotoUploading] = useState(false);
  const [stagedPhotos, setStagedPhotos] = useState([]); // File objects staged for existing pin (preview before upload)
  const [uploadResult, setUploadResult] = useState(null); // { type: 'success'|'error', count } — persistent feedback
  const createPhotoRef = useRef(null);  // camera input for new pin form
  const createGalleryRef = useRef(null); // gallery input for new pin form
  const existingPhotoRef = useRef(null); // camera input for existing pin popup
  const existingGalleryRef = useRef(null); // gallery input for existing pin popup
  // Segment/device status update state
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusPhotos, setStatusPhotos] = useState([]);
  const [statusPhotoUploading, setStatusPhotoUploading] = useState(false);
  const [qcMode, setQcMode] = useState(false);
  const [qcPassword, setQcPassword] = useState('');
  const [linkSegmentId, setLinkSegmentId] = useState(null);
  const [linkDeviceId, setLinkDeviceId] = useState(null);
  // Multi-select state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState(() => new Map());
  const [selectedDevices, setSelectedDevices] = useState(() => new Map());
  const [batchUpdating, setBatchUpdating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const multiSelectCount = selectedSegments.size + selectedDevices.size;
  const statusPhotoRef = useRef(null);
  const statusGalleryRef = useRef(null);
  const QC_PASSWORD = 'lytqc2026';
  const kbHeight = useKeyboardHeight();
  const commentInputRef = useRef(null);
  const gpsWatchRef = useRef(null);
  const gpsFollowingRef = useRef(false);
  const mapRef = useRef(null);
  const pageRef = useRef(null);
  const settingsRef = useRef(null);
  const initialPresetApplied = useRef(!!_saved.activePreset);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBS,
  });

  // Close settings on outside click
  useEffect(() => {
    if (!showSettings) return;
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [showSettings]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!pageRef.current) return;
    if (!document.fullscreenElement) {
      pageRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // GPS locate — toggle follow mode (map tracks user position)
  const locateMe = useCallback(() => {
    if (!navigator.geolocation) return;

    // If already following, toggle off
    if (gpsFollowing) {
      setGpsFollowing(false);
      gpsFollowingRef.current = false;
      if (gpsWatchRef.current != null) {
        navigator.geolocation.clearWatch(gpsWatchRef.current);
        gpsWatchRef.current = null;
      }
      return;
    }

    setLocating(true);
    // Clear existing watch
    if (gpsWatchRef.current != null) navigator.geolocation.clearWatch(gpsWatchRef.current);
    // Get initial position and center map
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setLocating(false);
        setGpsFollowing(true);
        gpsFollowingRef.current = true;
        if (mapRef.current) {
          mapRef.current.panTo(loc);
          mapRef.current.setZoom(17);
        }
        // Start watching for movement — panTo when following
        gpsWatchRef.current = navigator.geolocation.watchPosition(
          (p) => {
            const newLoc = { lat: p.coords.latitude, lng: p.coords.longitude };
            setUserLocation(newLoc);
            if (gpsFollowingRef.current && mapRef.current) {
              mapRef.current.panTo(newLoc);
            }
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 5000 }
        );
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [gpsFollowing]);

  // Cleanup GPS watch on unmount
  useEffect(() => {
    return () => { if (gpsWatchRef.current != null) navigator.geolocation.clearWatch(gpsWatchRef.current); };
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (!syncToast) return;
    const t = setTimeout(() => setSyncToast(null), 3500);
    return () => clearTimeout(t);
  }, [syncToast]);

  // Auto-sync when connectivity returns
  const prevOnline = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !prevOnline.current && getQueueCount() > 0) {
      (async () => {
        const result = await flushQueue();
        setPendingCount(getQueueCount());
        if (result.sent > 0) {
          setSyncToast({ type: 'green', message: `${result.sent} action${result.sent > 1 ? 's' : ''} synced` });
          // Silent data refresh to get server state
          if (projectId) loadData(projectId, selectedSection, true);
        }
      })();
    }
    prevOnline.current = isOnline;
  }, [isOnline, projectId, selectedSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Change map type
  const changeMapType = useCallback((type) => {
    setMapType(type);
    if (mapRef.current) mapRef.current.setMapTypeId(type);
  }, []);

  // Parse project ID and preset from URL on mount
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/[?&]project=([^&]+)/);
    if (match) {
      setProjectId(decodeURIComponent(match[1]));
    } else {
      setError('No project specified. Use URL like: #shared-map?project=BASTROP');
    }
    const presetMatch = hash.match(/[?&]preset=([^&]+)/);
    if (presetMatch) {
      const p = decodeURIComponent(presetMatch[1]);
      if (['construction', 'pulling', 'splicing'].includes(p)) setActivePreset(p);
    }
  }, []);

  // Load data (silent=true skips loading spinner for background refreshes)
  const loadData = useCallback(async (pid, section, silent = false) => {
    if (!pid) return;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const [projects, segs, devs, secs, notes] = await Promise.all([
        loadProjects(pid),
        loadSegments(pid, section || null),
        loadDevices(pid, section || null),
        loadSections(pid),
        loadMapNotes(pid, section || null, { includeAll: true }),
      ]);
      if (!projects || projects.length === 0) {
        if (!silent) setError(`Project "${pid}" not found`);
        if (!silent) setLoading(false);
        return;
      }
      setProject(projects[0]);
      setSegments(segs || []);
      setDevices(devs || []);
      setSections(secs || []);
      setMapNotes(notes || []);
      if (notes && notes.length > 0) cacheNotes(pid, notes);
      setLastRefresh(new Date());
    } catch (err) {
      if (!silent) setError('Failed to load project data');
      console.error('[SharedMapPage]', err);
      // Fall back to cached notes when offline
      const cached = getCachedNotes(pid);
      if (cached.length > 0 && mapNotes.length === 0) setMapNotes(cached);
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    if (projectId) loadData(projectId, selectedSection);
  }, [projectId, selectedSection, loadData]);

  // Silent auto-refresh every 60s (no loading spinner) + attempt queue flush
  useEffect(() => {
    if (!projectId) return;
    const interval = setInterval(async () => {
      // Try flushing queued actions even if online event never fired
      if (getQueueCount() > 0) {
        const result = await flushQueue();
        setPendingCount(getQueueCount());
        if (result.sent > 0) {
          setSyncToast({ type: 'green', message: `${result.sent} action${result.sent > 1 ? 's' : ''} synced` });
        }
      }
      loadData(projectId, selectedSection, true);
    }, 60000);
    return () => clearInterval(interval);
  }, [projectId, selectedSection, loadData]);

  // Load presets from DB on mount
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      const rows = await loadMapPresets(projectId);
      if (rows && rows.length > 0) {
        const merged = { ...DEFAULT_MAP_PRESETS };
        rows.forEach(r => {
          if (r.preset_name && r.config) {
            merged[r.preset_name] = typeof r.config === 'string' ? JSON.parse(r.config) : r.config;
          }
        });
        setPresets(merged);
      }
    })();
  }, [projectId]);

  // Apply preset config to state
  const applyPreset = useCallback((name) => {
    const cfg = presets[name] || DEFAULT_MAP_PRESETS[name];
    if (!cfg) return;
    setActivePreset(name);
    setPhase(cfg.phase || 'boring');
    setSelectedSegments(new Map());
    setSelectedDevices(new Map());
    setLytOnly(cfg.lytOnly !== false);
    setShowHandholes(cfg.showHandholes !== false);
    setShowFlowerpots(cfg.showFlowerpots !== false);
    setShowSplicePoints(cfg.showSplicePoints !== false);
    setShowPedestals(cfg.showPedestals !== false);
    setShowCabinets(cfg.showCabinets !== false);
    setShowFats(cfg.showFats === true);
    setShowAerial(cfg.showAerial !== false);
  }, [presets]);

  // Apply initial preset once presets are loaded
  useEffect(() => {
    if (initialPresetApplied.current) return;
    if (Object.keys(presets).length > 0) {
      applyPreset(activePreset);
      initialPresetApplied.current = true;
    }
  }, [presets, activePreset, applyPreset]);

  // Persist state to localStorage (separate per device type)
  useEffect(() => {
    const key = isMobile ? SHARED_MAP_KEY_MOBILE : SHARED_MAP_KEY_DESKTOP;
    localStorage.setItem(key, JSON.stringify({
      darkMode, selectedSection, showLegend, showStats, phase, activePreset,
      showHandholes, showFlowerpots, showSplicePoints, showPedestals, showCabinets, showFats, showAerial,
      lytOnly, mapType, showNotes,
    }));
  }, [isMobile, darkMode, selectedSection, showLegend, showStats, phase, activePreset,
    showHandholes, showFlowerpots, showSplicePoints, showPedestals, showCabinets, showFats, showAerial,
    lytOnly, mapType, showNotes]);

  // Client-side filtering
  const visibleSegments = useMemo(() => {
    if (!lytOnly) return segments;
    return segments.filter(s => s.lyt_assigned === true);
  }, [segments, lytOnly]);

  // Bounding box of LYT-assigned segments — defines "our work area"
  const lytBounds = useMemo(() => {
    const lytSegs = segments.filter(s => s.lyt_assigned);
    if (lytSegs.length === 0) return null;
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    lytSegs.forEach(s => {
      [parseFloat(s.gps_start_lat), parseFloat(s.gps_end_lat)].forEach(v => {
        if (!isNaN(v)) { if (v < minLat) minLat = v; if (v > maxLat) maxLat = v; }
      });
      [parseFloat(s.gps_start_lng), parseFloat(s.gps_end_lng)].forEach(v => {
        if (!isNaN(v)) { if (v < minLng) minLng = v; if (v > maxLng) maxLng = v; }
      });
    });
    // Pad by ~200m so nearby devices aren't clipped
    const PAD = 0.002;
    return { minLat: minLat - PAD, maxLat: maxLat + PAD, minLng: minLng - PAD, maxLng: maxLng + PAD };
  }, [segments]);

  const visibleDevices = useMemo(() => {
    // Devices only render when a section is selected
    if (!selectedSection) return [];
    return devices.filter(d => {
      const t = d.device_type;
      // When lytOnly is on, filter ALL devices to LYT's work area bounding box
      if (lytOnly) {
        if (d.lyt_assigned) { /* explicitly assigned — always show */ }
        else if (lytBounds) {
          const lat = parseFloat(d.gps_lat), lng = parseFloat(d.gps_lng);
          if (isNaN(lat) || isNaN(lng)) return false;
          if (lat < lytBounds.minLat || lat > lytBounds.maxLat || lng < lytBounds.minLng || lng > lytBounds.maxLng) return false;
        } else {
          return false; // no LYT segments at all — hide everything
        }
      }
      if (t === 'handhole' && !showHandholes) return false;
      if (t === 'flowerpot' && !showFlowerpots) return false;
      if ((t === 'splice_point' || t === 'splitter_1x4' || t === 'splitter_2x8') && !showSplicePoints) return false;
      if (t === 'pedestal' && !showPedestals) return false;
      if (t === 'cabinet' && !showCabinets) return false;
      if (t === 'fat' && !showFats) return false;
      return true;
    });
  }, [devices, lytOnly, lytBounds, showHandholes, showFlowerpots, showSplicePoints, showPedestals, showCabinets, showFats, selectedSection]);

  // Spread overlapping devices so stacked markers fan out
  const deviceOffsets = useMemo(() => spreadOverlapping(visibleDevices, 'gps_lat', 'gps_lng'), [visibleDevices]);

  // Initial center — computed once, never changes (prevents re-centering on refresh)
  const initialCenter = useRef(null);
  const mapCenter = useMemo(() => {
    if (initialCenter.current) return initialCenter.current;
    const pts = [];
    visibleSegments.forEach(s => {
      const sLat = parseFloat(s.gps_start_lat);
      const sLng = parseFloat(s.gps_start_lng);
      const eLat = parseFloat(s.gps_end_lat);
      const eLng = parseFloat(s.gps_end_lng);
      if (!isNaN(sLat) && !isNaN(sLng)) pts.push({ lat: sLat, lng: sLng });
      if (!isNaN(eLat) && !isNaN(eLng)) pts.push({ lat: eLat, lng: eLng });
    });
    visibleDevices.forEach(d => {
      const lat = parseFloat(d.gps_lat);
      const lng = parseFloat(d.gps_lng);
      if (!isNaN(lat) && !isNaN(lng)) pts.push({ lat, lng });
    });
    if (pts.length === 0) return { lat: 30.27, lng: -97.74 }; // Austin default
    const avgLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
    const avgLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
    const center = { lat: avgLat, lng: avgLng };
    initialCenter.current = center;
    return center;
  }, [visibleSegments, visibleDevices]);

  // Fit bounds on first load only
  const boundsApplied = useRef(false);
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (!window.google || boundsApplied.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;
    segments.forEach(s => {
      const sLat = parseFloat(s.gps_start_lat);
      const sLng = parseFloat(s.gps_start_lng);
      const eLat = parseFloat(s.gps_end_lat);
      const eLng = parseFloat(s.gps_end_lng);
      if (!isNaN(sLat) && !isNaN(sLng)) { bounds.extend({ lat: sLat, lng: sLng }); hasPoints = true; }
      if (!isNaN(eLat) && !isNaN(eLng)) { bounds.extend({ lat: eLat, lng: eLng }); hasPoints = true; }
    });
    devices.forEach(d => {
      const lat = parseFloat(d.gps_lat);
      const lng = parseFloat(d.gps_lng);
      if (!isNaN(lat) && !isNaN(lng)) { bounds.extend({ lat, lng }); hasPoints = true; }
    });
    if (hasPoints) {
      map.fitBounds(bounds, 40);
      boundsApplied.current = true;
    }
  }, [segments, devices]);

  // Phase status field
  const statusField = phase === 'pulling' ? 'pulling_status' : phase === 'splicing' ? 'splicing_status' : 'boring_status';

  // Stats (based on selected section, not viewport)
  const stats = useMemo(() => {
    const total = visibleSegments.length;
    const totalFootage = visibleSegments.reduce((s, seg) => s + (parseFloat(seg.footage) || 0), 0);
    const complete = visibleSegments.filter(s => {
      const st = s[statusField] || 'Not Started';
      return st === 'Complete' || st === 'QC Approved';
    }).length;
    const inProgress = visibleSegments.filter(s => (s[statusField] || 'Not Started') === 'In Progress').length;
    const notStarted = visibleSegments.filter(s => (s[statusField] || 'Not Started') === 'Not Started').length;
    const issue = visibleSegments.filter(s => (s[statusField] || 'Not Started') === 'Issue').length;
    const completedFootage = visibleSegments
      .filter(s => { const st = s[statusField] || 'Not Started'; return st === 'Complete' || st === 'QC Approved'; })
      .reduce((sum, seg) => sum + (parseFloat(seg.footage) || 0), 0);
    return { total, totalFootage, complete, inProgress, notStarted, issue, completedFootage };
  }, [visibleSegments, statusField]);

  // Colors — from centralized theme system
  const tc = getTheme(darkMode).colors;
  const bgColor = tc.bgAlt;
  const cardBg = tc.cardBg;
  const textColor = tc.text;
  const mutedColor = tc.textFaint;
  const borderColor = tc.borderSubtle;
  const accentColor = tc.accentPrimary;

  // Ownership helper — check if a note was created by the current user (phone match)
  const isOwnNote = useCallback((note) => {
    if (!commentPhone.trim()) return false;
    const noteIdentifier = normalizePhone(note.author_email || '');
    const myPhone = normalizePhone(commentPhone);
    return myPhone.length >= 7 && noteIdentifier === myPhone;
  }, [commentPhone]);

  const hasIdentity = commentName.trim().length >= 2 && normalizePhone(commentPhone).length >= 7;

  // Memoized note marker icons — prevents darting on mobile by avoiding
  // new icon objects on every render cycle
  const noteMarkerData = useMemo(() => {
    if (!isLoaded || !window.google) return [];
    return mapNotes.map(note => {
      const lat = parseFloat(note.lat);
      const lng = parseFloat(note.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      // When lytOnly, only show pins within LYT's work area
      if (lytOnly && lytBounds) {
        if (lat < lytBounds.minLat || lat > lytBounds.maxLat || lng < lytBounds.minLng || lng > lytBounds.maxLng) return null;
      }
      const unread = hasUnread(note);

      let iconW = 34, iconH = 44;
      const catInfo = PIN_CAT_MAP[note.category] || PIN_CAT_MAP.general;
      const pinColor = note.status === 'verified' ? '#9E9E9E' : catInfo.color;
      const letter = catInfo.letter;
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='34' height='44' viewBox='0 0 26 34'>`
        + `<path d='M13 1C6.4 1 1 6.4 1 13c0 9 12 20 12 20s12-11 12-20C25 6.4 19.6 1 13 1z' fill='${pinColor}' stroke='white' stroke-width='2'/>`
        + `<circle cx='13' cy='12' r='7' fill='white' fill-opacity='0.25'/>`
        + `<text x='13' y='16' text-anchor='middle' fill='white' font-size='13' font-weight='bold' font-family='Arial,sans-serif'>${letter}</text>`
        + `</svg>`;
      let iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

      if (unread) {
        const dotSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='${iconW + 8}' height='${iconH + 4}'>`
          + `<image href='${iconUrl}' x='4' y='4' width='${iconW}' height='${iconH}'/>`
          + `<circle cx='${iconW + 2}' cy='6' r='5' fill='#e53935' stroke='white' stroke-width='1.5'/>`
          + `</svg>`;
        iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(dotSvg)}`;
        iconW += 8;
        iconH += 4;
      }

      return {
        note,
        lat, lng, unread,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(iconW, iconH),
          anchor: new window.google.maps.Point(unread ? (iconW - 8) / 2 + 4 : iconW / 2, iconH),
        },
        zIndex: unread ? 950 : 900,
      };
    }).filter(Boolean);
  }, [mapNotes, darkMode, isLoaded, lytOnly, lytBounds]);

  // --- Pin action handlers ---

  const handleMapClick = useCallback((e) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    if (movingNote) {
      // Move mode: relocate the pin
      const phone = normalizePhone(commentPhone);
      moveMapNote(movingNote.note_id, lat, lng, phone).then(result => {
        if (result.success) {
          setSyncToast({ type: 'green', message: 'Pin moved' });
          loadData(projectId, selectedSection, true);
        } else {
          setSyncToast({ type: 'amber', message: result.error || 'Move failed' });
        }
        setMovingNote(null);
        setSelectedNote(null);
      });
      return;
    }

    if (placingPin) {
      setNewPinLocation({ lat, lng });
      setPlacingPin(false);
      return;
    }

    // Clear segment/device selection when tapping empty map (but NOT multi-selections)
    if (!multiSelectMode) {
      setSelectedSegment(null);
      setSelectedDevice(null);
    }
  }, [placingPin, movingNote, multiSelectMode, commentPhone, projectId, selectedSection, loadData]);

  // Build context object for Google Drive folder structure
  const buildDriveCtx = useCallback((note, overrides = {}) => {
    const ctx = {
      projectName: project?.name || '',
      projectId: projectId || '',
      section: selectedSection || '',
      category: note?.category || overrides.category || 'general',
      authorName: commentName || note?.created_by || '',
      noteId: note?.note_id || '',
      noteText: note?.text || overrides.noteText || '',
      status: note?.status || '',
      lat: note?.lat || overrides.lat,
      lng: note?.lng || overrides.lng,
      ...overrides,
    };
    // Attach linked segment/device info if available
    if (note?.segment_id) {
      const seg = segments.find(s => String(s.segment_id) === String(note.segment_id));
      if (seg) ctx.linkedItem = { type: 'Segment', name: seg.segment_name || seg.segment_id, id: seg.segment_id, status: seg.status, segmentType: seg.segment_type };
    }
    if (note?.device_id) {
      const dev = devices.find(d => String(d.device_id) === String(note.device_id));
      if (dev) ctx.linkedItem = { type: 'Device', name: dev.device_name || dev.device_id, id: dev.device_id, status: dev.status, deviceType: dev.device_type };
    }
    return ctx;
  }, [project, projectId, selectedSection, commentName, segments, devices]);

  const handleCreatePin = useCallback(async () => {
    if (!pinFormText.trim() || !newPinLocation || !hasIdentity) return;
    setPinFormSaving(true);
    const phone = normalizePhone(commentPhone);
    try {
      const result = await createMapNote({
        project_id: projectId,
        section: selectedSection || null,
        lat: newPinLocation.lat,
        lng: newPinLocation.lng,
        text: pinFormText.trim(),
        category: pinFormCategory,
        author_email: phone,
        author_name: commentName.trim(),
        author_role: 'contractor',
        visibility: 'shared',
        segment_id: linkSegmentId || undefined,
        device_id: linkDeviceId || undefined,
      });
      if (result.success) {
        // Upload pending photos to the newly created note
        const noteId = result.data?.note_id;
        let photosUploaded = 0;
        if (noteId && pendingPhotos.length > 0) {
          const driveCtx = buildDriveCtx(result.data, { noteText: pinFormText.trim(), category: pinFormCategory, lat: newPinLocation.lat, lng: newPinLocation.lng });
          for (const file of pendingPhotos) {
            const photoResult = await uploadNotePhoto(noteId, file, driveCtx);
            if (photoResult.success) photosUploaded++;
          }
        }
        if (pendingPhotos.length > 0 && photosUploaded === 0) {
          setSyncToast({ type: 'amber', message: 'Pin created but photo upload failed' });
        } else if (pendingPhotos.length > 0 && photosUploaded < pendingPhotos.length) {
          setSyncToast({ type: 'amber', message: `Pin created — ${photosUploaded}/${pendingPhotos.length} photos uploaded` });
        } else {
          setSyncToast({ type: 'green', message: photosUploaded > 0 ? `Pin created with ${photosUploaded} photo(s)` : 'Pin created' });
        }
        setNewPinLocation(null);
        setPinFormText('');
        setPinFormCategory('general');
        setPendingPhotos([]);
        setLinkSegmentId(null);
        setLinkDeviceId(null);
        loadData(projectId, selectedSection, true);
      } else {
        setSyncToast({ type: 'amber', message: result.error || 'Failed to create pin' });
      }
    } catch {
      setSyncToast({ type: 'amber', message: 'Failed to create pin' });
    }
    setPinFormSaving(false);
  }, [pinFormText, pinFormCategory, newPinLocation, hasIdentity, commentPhone, commentName, projectId, selectedSection, loadData, pendingPhotos, buildDriveCtx]);

  // Stage photos for existing pin (compress + preview before upload)
  const handleExistingPinPhoto = useCallback(async (e) => {
    if (!e.target.files?.length) return;
    const compressed = await Promise.all(Array.from(e.target.files).map(f => compressImage(f)));
    setStagedPhotos(prev => [...prev, ...compressed]);
    setUploadResult(null);
    if (existingPhotoRef.current) existingPhotoRef.current.value = '';
    if (existingGalleryRef.current) existingGalleryRef.current.value = '';
  }, []);

  // Confirm and upload staged photos
  const handleConfirmUpload = useCallback(async () => {
    if (!selectedNote || stagedPhotos.length === 0) return;
    setPhotoUploading(true);
    setUploadResult(null);
    try {
      let uploaded = 0;
      const driveCtx = buildDriveCtx(selectedNote);
      for (const file of stagedPhotos) {
        const result = await uploadNotePhoto(selectedNote.note_id, file, driveCtx);
        if (result.success) uploaded++;
      }
      if (uploaded === stagedPhotos.length) {
        setUploadResult({ type: 'success', count: uploaded });
        setSyncToast({ type: 'green', message: `${uploaded} photo(s) uploaded` });
      } else if (uploaded > 0) {
        setUploadResult({ type: 'partial', count: uploaded, total: stagedPhotos.length });
        setSyncToast({ type: 'amber', message: `${uploaded}/${stagedPhotos.length} photos uploaded` });
      } else {
        setUploadResult({ type: 'error' });
        setSyncToast({ type: 'amber', message: 'Photo upload failed — try a smaller image' });
      }
      setStagedPhotos([]);
      loadData(projectId, selectedSection, true);
    } catch {
      setUploadResult({ type: 'error' });
      setSyncToast({ type: 'amber', message: 'Photo upload failed' });
    } finally {
      setPhotoUploading(false);
    }
  }, [selectedNote, stagedPhotos, projectId, selectedSection, loadData, buildDriveCtx]);

  const handleEditPin = useCallback(async () => {
    if (!editingNote || !editText.trim()) return;
    setEditSaving(true);
    const phone = normalizePhone(commentPhone);
    try {
      const result = await editMapNote(editingNote.note_id, {
        text: editText.trim(),
        category: editCategory,
      }, phone, commentName.trim());
      if (result.success) {
        setSyncToast({ type: 'green', message: 'Pin updated' });
        setEditingNote(null);
        setSelectedNote(null);
        loadData(projectId, selectedSection, true);
      } else {
        setSyncToast({ type: 'amber', message: result.error || 'Edit failed' });
      }
    } catch {
      setSyncToast({ type: 'amber', message: 'Edit failed' });
    }
    setEditSaving(false);
  }, [editingNote, editText, editCategory, commentPhone, commentName, projectId, selectedSection, loadData]);

  const startMove = useCallback((note) => {
    setMovingNote(note);
    setSelectedNote(null);
  }, []);

  const startEdit = useCallback((note) => {
    setEditingNote(note);
    setEditText(note.text || '');
    setEditCategory(note.category || 'general');
  }, []);

  const cancelPinAction = useCallback(() => {
    setPlacingPin(false);
    setNewPinLocation(null);
    setPinFormText('');
    setPinFormCategory('general');
    setPendingPhotos([]);
    setMovingNote(null);
    setEditingNote(null);
    setLinkSegmentId(null);
    setLinkDeviceId(null);
  }, []);

  // --- Status sheet logic (shared between mobile bottom sheet + desktop InfoWindow) ---
  const statusSheetItem = selectedSegment || selectedDevice;
  const statusSheetIsSegment = !!selectedSegment;
  const statusSheetItemId = statusSheetItem ? (statusSheetIsSegment ? statusSheetItem.segment_id : statusSheetItem.device_id) : null;
  const statusSheetItemType = statusSheetIsSegment ? 'segment' : 'device';
  const statusSheetField = statusSheetItem
    ? (statusSheetIsSegment ? statusField : 'status')
    : null;
  const statusSheetCurrentStatus = statusSheetItem
    ? (statusSheetIsSegment ? (statusSheetItem[statusField] || 'Not Started') : (statusSheetItem.status || 'Not Started'))
    : null;
  const statusSheetLat = statusSheetItem
    ? (statusSheetIsSegment
      ? (parseFloat(statusSheetItem.gps_start_lat) + parseFloat(statusSheetItem.gps_end_lat)) / 2
      : parseFloat(statusSheetItem.gps_lat))
    : 0;
  const statusSheetLng = statusSheetItem
    ? (statusSheetIsSegment
      ? (parseFloat(statusSheetItem.gps_start_lng) + parseFloat(statusSheetItem.gps_end_lng)) / 2
      : parseFloat(statusSheetItem.gps_lng))
    : 0;

  const statusSheetLinkedNotes = useMemo(() => {
    if (!statusSheetItem) return [];
    return mapNotes.filter(n =>
      statusSheetIsSegment ? n.segment_id === statusSheetItem.segment_id : n.device_id === statusSheetItem.device_id
    );
  }, [mapNotes, statusSheetItem, statusSheetIsSegment]);

  const handleStatusPhotoCapture = useCallback(async (e) => {
    if (e.target.files?.length) {
      const compressed = await Promise.all(Array.from(e.target.files).map(f => compressImage(f)));
      setStatusPhotos(prev => [...prev, ...compressed]);
    }
    if (statusPhotoRef.current) statusPhotoRef.current.value = '';
    if (statusGalleryRef.current) statusGalleryRef.current.value = '';
  }, []);

  const handleItemStatusChange = useCallback(async (newStatus) => {
    if (!hasIdentity) { setIdentityError(true); return; }
    if (!statusSheetItem || !statusSheetField || !statusSheetItemId) return;
    setIdentityError(false);
    setStatusUpdating(true);

    let photoUrls = [];
    if (statusPhotos.length > 0 && isOnline) {
      setStatusPhotoUploading(true);
      const itemName = statusSheetIsSegment
        ? (statusSheetItem.segment_name || statusSheetItem.segment_id)
        : (statusSheetItem.device_name || statusSheetItem.device_id);
      const driveCtx = {
        projectName: project?.name || '',
        projectId: projectId || '',
        section: selectedSection || '',
        category: statusSheetIsSegment ? 'Segments' : 'Devices',
        authorName: commentName.trim(),
        noteId: statusSheetItemId,
        noteText: `${statusSheetItemType} ${itemName} → ${newStatus}`,
        status: newStatus,
        photoType: `status_${newStatus.toLowerCase().replace(/\s/g, '_')}`,
        lat: statusSheetLat,
        lng: statusSheetLng,
        linkedItem: {
          type: statusSheetIsSegment ? 'Segment' : 'Device',
          name: itemName,
          id: statusSheetItemId,
          status: newStatus,
          ...(statusSheetIsSegment
            ? { segmentType: statusSheetItem.segment_type }
            : { deviceType: statusSheetItem.device_type }),
        },
      };
      for (const file of statusPhotos) {
        const result = await uploadPhoto(file, driveCtx);
        if (result.success && result.url) photoUrls.push(result.url);
      }
      setStatusPhotoUploading(false);
    } else if (statusPhotos.length > 0 && !isOnline) {
      setSyncToast({ type: 'amber', message: 'Photos skipped — no network' });
    }

    const payload = {
      item_type: statusSheetItemType,
      item_id: statusSheetItemId,
      project_id: projectId,
      field: statusSheetField,
      new_value: newStatus,
      actor_name: commentName.trim(),
      actor_phone: normalizePhone(commentPhone),
      photo_urls: photoUrls.length > 0 ? JSON.stringify(photoUrls) : null,
    };

    const doOptimistic = () => {
      if (statusSheetIsSegment) {
        setSegments(prev => prev.map(s =>
          s.segment_id === statusSheetItemId ? { ...s, [statusSheetField]: newStatus, _pending: true } : s
        ));
      } else {
        setDevices(prev => prev.map(d =>
          d.device_id === statusSheetItemId ? { ...d, status: newStatus, _pending: true } : d
        ));
      }
      setSelectedSegment(null);
      setSelectedDevice(null);
      setStatusPhotos([]);
    };

    if (!isOnline) {
      enqueueAction('status_update', payload);
      setPendingCount(getQueueCount());
      doOptimistic();
      setSyncToast({ type: 'amber', message: 'Status change saved offline' });
      setStatusUpdating(false);
      return;
    }

    try {
      const result = await updateItemStatus(statusSheetItemType, statusSheetItemId, projectId, statusSheetField, newStatus, commentName.trim(), normalizePhone(commentPhone), photoUrls.length > 0 ? photoUrls : null);
      if (result.success) {
        if (statusSheetIsSegment) {
          setSegments(prev => prev.map(s =>
            s.segment_id === statusSheetItemId ? { ...s, ...result.data } : s
          ));
        } else {
          setDevices(prev => prev.map(d =>
            d.device_id === statusSheetItemId ? { ...d, ...result.data } : d
          ));
        }
        setSyncToast({ type: 'green', message: `${statusSheetIsSegment ? 'Segment' : 'Device'} marked ${newStatus}` });
        setSelectedSegment(null);
        setSelectedDevice(null);
        setStatusPhotos([]);
      } else {
        setSyncToast({ type: 'amber', message: result.error || 'Update failed' });
      }
    } catch (err) {
      console.error('[SharedMap] Status update failed, queuing offline:', err);
      enqueueAction('status_update', payload);
      setPendingCount(getQueueCount());
      doOptimistic();
      setSyncToast({ type: 'amber', message: 'Status change saved offline' });
    } finally {
      setStatusUpdating(false);
    }
  }, [hasIdentity, statusSheetItem, statusSheetField, statusSheetItemId, statusSheetItemType, statusSheetIsSegment, statusPhotos, isOnline, projectId, commentName, commentPhone, project, selectedSection, statusSheetLat, statusSheetLng]);

  const closeStatusSheet = useCallback(() => {
    setSelectedSegment(null);
    setSelectedDevice(null);
    setIdentityError(false);
    setStatusPhotos([]);
  }, []);

  // --- Batch status change handler (multi-select mode) ---
  const handleBatchStatusChange = useCallback(async (newStatus) => {
    if (!hasIdentity) { setIdentityError(true); return; }
    const allItems = [];
    selectedSegments.forEach((seg, id) => {
      allItems.push({ type: 'segment', id, item: seg, field: statusField });
    });
    selectedDevices.forEach((dev, id) => {
      allItems.push({ type: 'device', id, item: dev, field: 'status' });
    });
    if (allItems.length === 0) return;
    setIdentityError(false);
    setBatchUpdating(true);
    setBatchProgress({ done: 0, total: allItems.length });
    let successCount = 0;

    for (let i = 0; i < allItems.length; i++) {
      const { type, id, field } = allItems[i];
      setBatchProgress({ done: i, total: allItems.length });
      const payload = {
        item_type: type === 'segment' ? 'segment' : 'device',
        item_id: id,
        project_id: projectId,
        field,
        new_value: newStatus,
        actor_name: commentName.trim(),
        actor_phone: normalizePhone(commentPhone),
        photo_urls: null,
      };

      // Optimistic update immediately
      if (type === 'segment') {
        setSegments(prev => prev.map(s =>
          s.segment_id === id ? { ...s, [field]: newStatus, _pending: !isOnline } : s
        ));
      } else {
        setDevices(prev => prev.map(d =>
          d.device_id === id ? { ...d, status: newStatus, _pending: !isOnline } : d
        ));
      }

      if (!isOnline) {
        enqueueAction('status_update', payload);
        setPendingCount(getQueueCount());
        successCount++;
        continue;
      }

      try {
        const result = await updateItemStatus(
          payload.item_type, id, projectId, field, newStatus,
          commentName.trim(), normalizePhone(commentPhone), null
        );
        if (result.success) {
          if (type === 'segment') {
            setSegments(prev => prev.map(s =>
              s.segment_id === id ? { ...s, ...result.data } : s
            ));
          } else {
            setDevices(prev => prev.map(d =>
              d.device_id === id ? { ...d, ...result.data } : d
            ));
          }
          successCount++;
        }
      } catch (err) {
        console.error('[SharedMap] Batch item failed, queuing offline:', err);
        enqueueAction('status_update', payload);
        setPendingCount(getQueueCount());
        successCount++;
      }
    }

    setBatchProgress({ done: allItems.length, total: allItems.length });
    setSelectedSegments(new Map());
    setSelectedDevices(new Map());
    setBatchUpdating(false);
    if (!isOnline) {
      setSyncToast({ type: 'amber', message: `${successCount} item${successCount !== 1 ? 's' : ''} saved offline` });
    } else {
      setSyncToast({ type: 'green', message: `${successCount} item${successCount !== 1 ? 's' : ''} marked ${newStatus}` });
    }
  }, [hasIdentity, selectedSegments, selectedDevices, statusField, projectId, commentName, commentPhone, isOnline]);

  // --- Render ---

  if (loadError) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>Google Maps failed to load</div>;
  }

  if (error && !projectId) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgColor, color: textColor, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            <span style={{ color: tc.logoLY }}>ly</span>
            <span style={{ color: tc.logoT }}>t</span>
          </div>
          <p style={{ color: mutedColor, fontSize: 14 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageRef} style={{ height: '100dvh', background: bgColor, color: textColor, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <header style={{
        padding: isMobile ? '12px 16px' : '8px 16px',
        background: tc.topBarBg,
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        zIndex: 20,
      }}>
        {/* Left: logo + project/section name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: isMobile ? 1 : undefined }}>
          <div style={{ fontSize: isMobile ? '1.3rem' : '1.3rem', fontWeight: 700, flexShrink: 0 }}>
            <span style={{ color: tc.logoLY }}>ly</span>
            <span style={{ color: tc.logoT }}>t</span>
          </div>
          {project && (
            <span style={{ fontWeight: 600, fontSize: isMobile ? 15 : 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedSection || project.name || project.project_id}
            </span>
          )}
        </div>

        {/* Center: preset buttons (desktop only — moved to settings on mobile) */}
        {!isMobile && (
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
            {[
              { key: 'construction', label: 'Construction' },
              { key: 'pulling', label: 'Pulling' },
              { key: 'splicing', label: 'Splicing' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                style={{
                  padding: '5px 12px',
                  fontSize: 11,
                  fontWeight: activePreset === p.key ? 700 : 500,
                  background: activePreset === p.key ? accentColor : 'transparent',
                  color: activePreset === p.key ? '#fff' : mutedColor,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Right: settings gear */}
        <div ref={settingsRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowSettings(!showSettings)}
            style={{
              width: isMobile ? 44 : undefined, height: isMobile ? 44 : undefined,
              padding: isMobile ? 0 : 6, borderRadius: 8, border: `1px solid ${borderColor}`,
              background: showSettings ? accentColor : 'transparent',
              color: showSettings ? '#fff' : mutedColor, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Settings size={isMobile ? 20 : 16} />
            {qcMode && (
              <div style={{
                position: 'absolute', top: -2, right: -2,
                width: 8, height: 8, borderRadius: '50%',
                background: '#2196F3', border: '1.5px solid ' + (showSettings ? accentColor : (darkMode ? tc.topBarBg : '#fff')),
              }} />
            )}
          </button>

          {/* Settings — bottom sheet on mobile, dropdown on desktop */}
          {showSettings && isMobile && (
            <div onClick={() => setShowSettings(false)} style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99,
              background: 'rgba(0,0,0,0.4)',
            }} />
          )}
          {showSettings && (
            <div style={isMobile ? {
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
              background: darkMode ? 'rgba(30,41,59,0.92)' : 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '20px 20px 0 0',
              padding: '8px 20px 20px', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.25)',
              maxHeight: '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            } : {
              position: 'absolute', top: '100%', right: 0, marginTop: 6,
              background: darkMode ? tc.cardBg : '#ffffff',
              border: `1px solid ${borderColor}`,
              borderRadius: 10, padding: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              minWidth: 220, zIndex: 100,
            }}>
              {/* Drag handle (mobile only) */}
              {isMobile && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 12px' }}>
                  <div style={{ width: 36, height: 4, borderRadius: 2, background: mutedColor, opacity: 0.4 }} />
                </div>
              )}
              {/* View preset (mobile only — in header on desktop) */}
              {isMobile && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>View</div>
                  <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
                    {[
                      { key: 'construction', label: 'Build' },
                      { key: 'pulling', label: 'Pull' },
                      { key: 'splicing', label: 'Splice' },
                    ].map(p => (
                      <button key={p.key} onClick={() => applyPreset(p.key)} style={{
                        flex: 1, padding: '0', minHeight: 44, fontSize: 13, fontWeight: activePreset === p.key ? 700 : 500, border: 'none', cursor: 'pointer',
                        background: activePreset === p.key ? accentColor : 'transparent',
                        color: activePreset === p.key ? '#fff' : mutedColor,
                      }}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Map Type */}
              <div style={{ marginBottom: isMobile ? 14 : 12 }}>
                <div style={{ fontSize: isMobile ? 11 : 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: isMobile ? 8 : 6 }}>Map Type</div>
                <div style={{ display: 'flex', borderRadius: isMobile ? 8 : 6, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
                  {[
                    { key: 'roadmap', label: 'Map' },
                    { key: 'hybrid', label: 'Satellite' },
                  ].map(t => (
                    <button key={t.key} onClick={() => changeMapType(t.key)} style={{
                      flex: 1, padding: isMobile ? '0' : '6px 0', minHeight: isMobile ? 44 : undefined, fontSize: isMobile ? 13 : 11, fontWeight: mapType === t.key ? 700 : 500, border: 'none', cursor: 'pointer',
                      background: mapType === t.key ? accentColor : 'transparent',
                      color: mapType === t.key ? '#fff' : mutedColor,
                    }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Section filter */}
              {sections.length > 0 && (
                <div style={{ marginBottom: isMobile ? 14 : 12 }}>
                  <div style={{ fontSize: isMobile ? 11 : 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: isMobile ? 8 : 6 }}>Section</div>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={selectedSection}
                      onChange={e => { setSelectedSection(e.target.value); setSelectedSegments(new Map()); setSelectedDevices(new Map()); }}
                      style={{
                        width: '100%', padding: isMobile ? '10px 32px 10px 12px' : '6px 28px 6px 8px', fontSize: isMobile ? 14 : 12,
                        borderRadius: isMobile ? 8 : 6, border: `1px solid ${borderColor}`,
                        background: darkMode ? tc.inputBg : '#f8fafc', color: textColor,
                        cursor: 'pointer', appearance: 'none', minHeight: isMobile ? 44 : undefined,
                      }}
                    >
                      <option value="">All Sections</option>
                      {sections.map(s => (
                        <option key={s.section} value={s.section}>{s.section} ({s.count})</option>
                      ))}
                    </select>
                    <ChevronDown size={isMobile ? 16 : 12} style={{ position: 'absolute', right: isMobile ? 12 : 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: mutedColor }} />
                  </div>
                </div>
              )}

              {/* Toggles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 8 }}>
                <div style={{ fontSize: isMobile ? 11 : 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>Display</div>

                {/* Dark mode toggle */}
                <button onClick={() => setDarkMode(!darkMode)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '12px 16px' : '6px 8px', borderRadius: isMobile ? 10 : 6,
                  border: `1px solid ${borderColor}`, background: 'transparent', color: textColor,
                  cursor: 'pointer', fontSize: isMobile ? 14 : 12, width: '100%', textAlign: 'left', minHeight: isMobile ? 48 : undefined,
                }}>
                  {darkMode ? <Sun size={isMobile ? 18 : 14} /> : <Moon size={isMobile ? 18 : 14} />}
                  <span style={{ flex: 1 }}>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>

                {/* Legend toggle */}
                <button onClick={() => setShowLegend(!showLegend)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '12px 16px' : '6px 8px', borderRadius: isMobile ? 10 : 6,
                  border: `1px solid ${borderColor}`,
                  background: showLegend ? (darkMode ? 'rgba(102,126,234,0.15)' : 'rgba(0,119,182,0.08)') : 'transparent',
                  color: textColor, cursor: 'pointer', fontSize: isMobile ? 14 : 12, width: '100%', textAlign: 'left', minHeight: isMobile ? 48 : undefined,
                }}>
                  <Layers size={isMobile ? 18 : 14} />
                  <span style={{ flex: 1 }}>Legend</span>
                  {showLegend && <span style={{ fontSize: isMobile ? 12 : 10, color: accentColor, fontWeight: 700 }}>ON</span>}
                </button>

                {/* Stats toggle */}
                <button onClick={() => setShowStats(!showStats)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '12px 16px' : '6px 8px', borderRadius: isMobile ? 10 : 6,
                  border: `1px solid ${borderColor}`,
                  background: showStats ? (darkMode ? 'rgba(102,126,234,0.15)' : 'rgba(0,119,182,0.08)') : 'transparent',
                  color: textColor, cursor: 'pointer', fontSize: isMobile ? 14 : 12, width: '100%', textAlign: 'left', minHeight: isMobile ? 48 : undefined,
                }}>
                  <BarChart3 size={isMobile ? 18 : 14} />
                  <span style={{ flex: 1 }}>Stats Bar</span>
                  {showStats && <span style={{ fontSize: isMobile ? 12 : 10, color: accentColor, fontWeight: 700 }}>ON</span>}
                </button>

                {/* Notes toggle */}
                <button onClick={() => { setShowNotes(!showNotes); if (showNotes) setSelectedNote(null); }} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '12px 16px' : '6px 8px', borderRadius: isMobile ? 10 : 6,
                  border: `1px solid ${borderColor}`,
                  background: showNotes ? (darkMode ? 'rgba(102,126,234,0.15)' : 'rgba(0,119,182,0.08)') : 'transparent',
                  color: textColor, cursor: 'pointer', fontSize: isMobile ? 14 : 12, width: '100%', textAlign: 'left', minHeight: isMobile ? 48 : undefined,
                }}>
                  <MapPin size={isMobile ? 18 : 14} />
                  <span style={{ flex: 1 }}>Notes</span>
                  {showNotes && <span style={{ fontSize: isMobile ? 12 : 10, color: accentColor, fontWeight: 700 }}>ON</span>}
                </button>
              </div>

              {/* Device Toggles */}
              <div style={{ marginTop: isMobile ? 14 : 12, paddingTop: isMobile ? 14 : 10, borderTop: `1px solid ${borderColor}` }}>
                <div style={{ fontSize: isMobile ? 11 : 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: isMobile ? 8 : 6 }}>Devices{!selectedSection ? ' (select a section)' : ''}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 6 : 4 }}>
                  {[
                    { label: 'Handholes', type: 'handhole', show: showHandholes, toggle: () => setShowHandholes(!showHandholes) },
                    { label: 'Flowerpots', type: 'flowerpot', show: showFlowerpots, toggle: () => setShowFlowerpots(!showFlowerpots) },
                    { label: 'Splice Points', type: 'splice_point', show: showSplicePoints, toggle: () => setShowSplicePoints(!showSplicePoints) },
                    { label: 'Pedestals / Cabinets', type: 'pedestal', show: showPedestals, toggle: () => { setShowPedestals(!showPedestals); setShowCabinets(!showCabinets); } },
                    { label: 'FATs', type: 'fat', show: showFats, toggle: () => setShowFats(!showFats) },
                  ].map(d => (
                    <button key={d.type} onClick={d.toggle} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '10px 16px' : '5px 8px', borderRadius: isMobile ? 10 : 6,
                      border: `1px solid ${borderColor}`,
                      background: d.show ? (darkMode ? 'rgba(102,126,234,0.15)' : 'rgba(0,119,182,0.08)') : 'transparent',
                      color: textColor, cursor: 'pointer', fontSize: isMobile ? 14 : 12, width: '100%', textAlign: 'left', minHeight: isMobile ? 44 : undefined,
                    }}>
                      <img src={buildDeviceSvg(d.type, darkMode)} alt="" width={isMobile ? 18 : 14} height={isMobile ? 18 : 14} style={{ opacity: d.show ? 1 : 0.4 }} />
                      <span style={{ flex: 1, opacity: d.show ? 1 : 0.5 }}>{d.label}</span>
                      {d.show && <span style={{ fontSize: isMobile ? 12 : 10, color: accentColor, fontWeight: 700 }}>ON</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Your identity (for comments + status changes) */}
              <div style={{ marginTop: isMobile ? 14 : 12, paddingTop: isMobile ? 14 : 10, borderTop: `1px solid ${borderColor}` }}>
                <div style={{ fontSize: isMobile ? 11 : 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: isMobile ? 8 : 6 }}>Your Identity</div>
                <input
                  placeholder="Your name"
                  value={commentName}
                  onChange={e => {
                    setCommentName(e.target.value);
                    try { localStorage.setItem('lyt-commenter-name', e.target.value); } catch {}
                  }}
                  style={{
                    width: '100%', padding: isMobile ? '10px 14px' : '6px 8px', fontSize: isMobile ? 16 : 12, borderRadius: isMobile ? 10 : 6, boxSizing: 'border-box',
                    border: `1px solid ${borderColor}`, background: darkMode ? tc.inputBg : '#f8fafc',
                    color: textColor, marginBottom: isMobile ? 10 : 6, outline: 'none', minHeight: isMobile ? 44 : undefined,
                  }}
                />
                <input
                  placeholder="Phone number"
                  value={commentPhone}
                  onChange={e => {
                    setCommentPhone(e.target.value);
                    try { localStorage.setItem('lyt-commenter-phone', e.target.value); } catch {}
                  }}
                  type="tel"
                  style={{
                    width: '100%', padding: isMobile ? '10px 14px' : '6px 8px', fontSize: isMobile ? 16 : 12, borderRadius: isMobile ? 10 : 6, boxSizing: 'border-box',
                    border: `1px solid ${borderColor}`, background: darkMode ? tc.inputBg : '#f8fafc',
                    color: textColor, outline: 'none', minHeight: isMobile ? 44 : undefined,
                  }}
                />
              </div>

              {/* QC Mode */}
              <div style={{ marginTop: isMobile ? 14 : 12, paddingTop: isMobile ? 14 : 10, borderTop: `1px solid ${borderColor}` }}>
                <div style={{ fontSize: isMobile ? 11 : 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: isMobile ? 8 : 6 }}>QC Mode</div>
                {qcMode ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2196F3' }} />
                    <span style={{ fontSize: isMobile ? 14 : 12, fontWeight: 600, color: '#2196F3', flex: 1 }}>QC Mode Active</span>
                    <button onClick={() => setQcMode(false)} style={{
                      padding: isMobile ? '8px 16px' : '5px 10px', borderRadius: isMobile ? 8 : 6,
                      border: `1px solid ${borderColor}`, background: 'transparent',
                      color: mutedColor, cursor: 'pointer', fontSize: isMobile ? 13 : 11,
                      minHeight: isMobile ? 44 : undefined,
                    }}>Deactivate</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      placeholder="QC password"
                      type="password"
                      value={qcPassword}
                      onChange={e => setQcPassword(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && qcPassword === QC_PASSWORD) {
                          setQcMode(true); setQcPassword('');
                        }
                      }}
                      style={{
                        flex: 1, padding: isMobile ? '10px 14px' : '6px 8px', fontSize: isMobile ? 16 : 12,
                        borderRadius: isMobile ? 10 : 6, boxSizing: 'border-box',
                        border: `1px solid ${borderColor}`, background: darkMode ? tc.inputBg : '#f8fafc',
                        color: textColor, outline: 'none', minHeight: isMobile ? 44 : undefined,
                      }}
                    />
                    <button onClick={() => {
                      if (qcPassword === QC_PASSWORD) { setQcMode(true); setQcPassword(''); }
                      else { setSyncToast({ type: 'amber', message: 'Wrong QC password' }); setQcPassword(''); }
                    }} style={{
                      padding: isMobile ? '8px 16px' : '5px 10px', borderRadius: isMobile ? 8 : 6,
                      border: 'none', background: accentColor, color: '#fff',
                      cursor: 'pointer', fontSize: isMobile ? 13 : 11, fontWeight: 600,
                      minHeight: isMobile ? 44 : undefined,
                    }}>Unlock</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Stats bar */}
      {showStats && !loading && visibleSegments.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? 8 : 12,
          padding: isMobile ? '10px 16px' : '10px 16px',
          background: darkMode ? 'rgba(17,34,64,0.85)' : 'rgba(241,245,249,0.85)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${borderColor}`,
          alignItems: 'center',
        }}>
          {/* Progress bar */}
          <div style={{ flex: '1 1 auto', minWidth: isMobile ? 0 : 200, width: isMobile ? '100%' : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: isMobile ? 13 : 11, fontWeight: 600, textTransform: 'capitalize' }}>{phase} Progress</span>
              <span style={{ fontSize: isMobile ? 13 : 11, fontWeight: 700, color: accentColor }}>{pct(stats.completedFootage, stats.totalFootage)}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                borderRadius: 4,
                width: `${pct(stats.completedFootage, stats.totalFootage)}%`,
                background: `linear-gradient(90deg, ${STATUS_COLORS.COMPLETE}, ${STATUS_COLORS.QC_APPROVED})`,
                transition: 'width 0.5s ease',
              }} />
            </div>
          </div>

          {/* Footage chips */}
          {[
            { label: 'Total', value: `${Math.round(stats.totalFootage).toLocaleString()} ft` },
            { label: 'Complete', value: `${Math.round(stats.completedFootage).toLocaleString()} ft`, color: STATUS_COLORS.COMPLETE },
            { label: 'Remaining', value: `${Math.round(stats.totalFootage - stats.completedFootage).toLocaleString()} ft`, color: STATUS_COLORS.NOT_STARTED },
          ].map(chip => (
            <div key={chip.label} style={{
              display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 6, padding: isMobile ? '6px 12px' : '4px 10px',
              borderRadius: 10, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)', border: `1px solid ${borderColor}`,
              flex: isMobile ? '1 1 auto' : undefined,
              justifyContent: isMobile ? 'center' : undefined,
            }}>
              {chip.color && <div style={{ width: 10, height: 10, borderRadius: '50%', background: chip.color }} />}
              <span style={{ fontSize: isMobile ? 12 : 11, color: mutedColor }}>{chip.label}:</span>
              <span style={{ fontSize: isMobile ? 13 : 12, fontWeight: 700 }}>{chip.value}</span>
            </div>
          ))}

          {lastRefresh && !isMobile && (
            <span style={{ fontSize: 10, color: mutedColor, marginLeft: 'auto' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      {/* Map area */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10, display: 'flex', flexDirection: 'column',
            background: darkMode ? 'rgba(13,27,42,0.92)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)',
          }}>
            {/* Shimmer skeleton — stats bar placeholder */}
            <div style={{ padding: isMobile ? '10px 16px' : '10px 16px', display: 'flex', gap: 8 }}>
              <div className="shimmer-bar" style={{ height: 28, flex: 1, borderRadius: 8, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />
              <div className="shimmer-bar" style={{ height: 28, width: 80, borderRadius: 8, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />
              <div className="shimmer-bar" style={{ height: 28, width: 80, borderRadius: 8, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />
            </div>
            {/* Shimmer skeleton — map area outline */}
            <div style={{ flex: 1, margin: isMobile ? '0 16px 16px' : '0 16px 16px', borderRadius: 12, background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }} className="shimmer-bar" />
            {/* Spinner overlay */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: accentColor }} />
                <p style={{ marginTop: 12, fontSize: 13, color: mutedColor }}>Loading project data...</p>
              </div>
            </div>
          </div>
        )}

        {error && projectId && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: darkMode ? 'rgba(13,27,42,0.9)' : 'rgba(255,255,255,0.9)',
          }}>
            <div style={{ textAlign: 'center', padding: 24 }}>
              <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Error</p>
              <p style={{ fontSize: 13, color: mutedColor }}>{error}</p>
              <button
                onClick={() => loadData(projectId, selectedSection)}
                style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, border: 'none', background: accentColor, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={14}
            onLoad={(map) => {
              map.setMapTypeId(mapType);
              onMapLoad(map);
            }}
            onClick={handleMapClick}
            onDragStart={() => {
              if (gpsFollowing) {
                setGpsFollowing(false);
                gpsFollowingRef.current = false;
              }
            }}
            options={{
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: false,
              scaleControl: false,
              rotateControl: false,
              cameraControl: false,
              keyboardShortcuts: false,
              styles: darkMode ? darkMapStyle : hidePoiStyle,
              gestureHandling: 'greedy',
            }}
          >
            {/* Segment polylines */}
            {visibleSegments.map(seg => {
              const sLat = parseFloat(seg.gps_start_lat);
              const sLng = parseFloat(seg.gps_start_lng);
              const eLat = parseFloat(seg.gps_end_lat);
              const eLng = parseFloat(seg.gps_end_lng);
              if (isNaN(sLat) || isNaN(sLng) || isNaN(eLat) || isNaN(eLng)) return null;
              const status = seg[statusField] || 'Not Started';
              const isSelected = multiSelectMode ? selectedSegments.has(seg.segment_id) : selectedSegment?.segment_id === seg.segment_id;
              const path = [{ lat: sLat, lng: sLng }, { lat: eLat, lng: eLng }];
              const segClick = () => {
                if (placingPin || movingNote) return;
                if (multiSelectMode) {
                  setSelectedSegments(prev => {
                    const next = new Map(prev);
                    if (next.has(seg.segment_id)) next.delete(seg.segment_id);
                    else next.set(seg.segment_id, seg);
                    return next;
                  });
                  return;
                }
                setSelectedSegment(seg);
                setSelectedDevice(null);
                setSelectedNote(null);
                setStatusPhotos([]);
              };
              if (isSelected) {
                const glowOuter = multiSelectMode ? 10 : 16;
                const glowInner = multiSelectMode ? 7 : 10;
                return (
                  <React.Fragment key={seg.segment_id}>
                    {/* Outer glow — wide, faint, white/cyan */}
                    <Polyline
                      path={path}
                      options={{ strokeColor: '#00e5ff', strokeOpacity: 0.25, strokeWeight: glowOuter, zIndex: 98 }}
                    />
                    {/* Inner glow — medium, brighter */}
                    <Polyline
                      path={path}
                      options={{ strokeColor: '#00e5ff', strokeOpacity: 0.5, strokeWeight: glowInner, zIndex: 99 }}
                    />
                    {/* Actual segment line on top */}
                    <Polyline
                      path={path}
                      options={{ strokeColor: getStatusColor(status), strokeOpacity: 1, strokeWeight: 5, zIndex: 100 }}
                      onClick={segClick}
                    />
                  </React.Fragment>
                );
              }
              return (
                <Polyline
                  key={seg.segment_id}
                  path={path}
                  options={{ strokeColor: getStatusColor(status), strokeOpacity: 0.9, strokeWeight: 4, zIndex: 1 }}
                  onClick={segClick}
                />
              );
            })}

            {/* Device markers */}
            {visibleDevices.map((d, dIdx) => {
              const lat = parseFloat(d.gps_lat);
              const lng = parseFloat(d.gps_lng);
              if (isNaN(lat) || isNaN(lng)) return null;
              const off = deviceOffsets[dIdx] || { dlat: 0, dlng: 0 };
              const posLat = lat + off.dlat;
              const posLng = lng + off.dlng;
              const isSelected = multiSelectMode ? selectedDevices.has(d.device_id) : selectedDevice?.device_id === d.device_id;
              let iconUrl, iconSize, iconAnchor;
              if (isSelected && multiSelectMode) {
                // Multi-select: static cyan ring (no pulsing animation)
                const sz = 40;
                const c = sz / 2;
                const ringSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='${sz}' height='${sz}'>
                  <circle cx='${c}' cy='${c}' r='17' fill='rgba(0,229,255,0.12)' stroke='#00e5ff' stroke-width='2'/>
                  ${d.device_type === 'handhole'
                    ? `<rect x='${c-7}' y='${c-7}' width='14' height='14' rx='3' fill='${darkMode ? '#42a5f5' : '#1565C0'}' stroke='white' stroke-width='1.5'/><text x='${c}' y='${c+4}' text-anchor='middle' font-size='10' font-weight='bold' font-family='Arial' fill='white'>H</text>`
                    : d.device_type === 'flowerpot'
                    ? `<circle cx='${c}' cy='${c}' r='8' fill='${darkMode ? '#ffa726' : '#E65100'}' stroke='white' stroke-width='1.5'/><text x='${c}' y='${c+4}' text-anchor='middle' font-size='10' font-weight='bold' font-family='Arial' fill='white'>F</text>`
                    : (d.device_type === 'splice_point' || d.device_type === 'splitter_1x4' || d.device_type === 'splitter_2x8')
                    ? `<polygon points='${c-8},${c-4} ${c},${c} ${c-8},${c+4}' fill='${darkMode ? '#ef5350' : '#D32F2F'}' stroke='white' stroke-width='1'/><polygon points='${c+8},${c-4} ${c},${c} ${c+8},${c+4}' fill='${darkMode ? '#ef5350' : '#D32F2F'}' stroke='white' stroke-width='1'/>`
                    : (d.device_type === 'pedestal' || d.device_type === 'cabinet')
                    ? `<rect x='${c-5}' y='${c-7}' width='10' height='14' rx='1' fill='${darkMode ? '#66bb6a' : '#2E7D32'}' stroke='white' stroke-width='1.5'/>`
                    : d.device_type === 'fat'
                    ? `<polygon points='${c},${c-8} ${c+8},${c} ${c},${c+8} ${c-8},${c}' fill='${darkMode ? '#ab47bc' : '#6A1B9A'}' stroke='white' stroke-width='1.5'/><circle cx='${c}' cy='${c}' r='2.5' fill='white'/>`
                    : `<circle cx='${c}' cy='${c}' r='6' fill='#999' stroke='white' stroke-width='1.5'/>`
                  }
                </svg>`;
                iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(ringSvg)}`;
                iconSize = new window.google.maps.Size(sz, sz);
                iconAnchor = new window.google.maps.Point(sz / 2, sz / 2);
              } else if (isSelected) {
                // Beacon SVG — pulsing rings + device shape in center
                const sz = 48;
                const c = sz / 2;
                const beaconSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='${sz}' height='${sz}'>
                  <style>
                    @keyframes ping1 { 0% { r: 8; opacity: 0.6; } 100% { r: 22; opacity: 0; } }
                    @keyframes ping2 { 0% { r: 8; opacity: 0.4; } 100% { r: 22; opacity: 0; } }
                  </style>
                  <circle cx='${c}' cy='${c}' r='8' fill='none' stroke='#00e5ff' stroke-width='2' style='animation: ping1 1.5s ease-out infinite;'/>
                  <circle cx='${c}' cy='${c}' r='8' fill='none' stroke='#00e5ff' stroke-width='1.5' style='animation: ping2 1.5s ease-out 0.5s infinite;'/>
                  <circle cx='${c}' cy='${c}' r='10' fill='rgba(0,229,255,0.15)' stroke='#00e5ff' stroke-width='1.5'/>
                  ${d.device_type === 'handhole'
                    ? `<rect x='${c-7}' y='${c-7}' width='14' height='14' rx='3' fill='${darkMode ? '#42a5f5' : '#1565C0'}' stroke='white' stroke-width='1.5'/><text x='${c}' y='${c+4}' text-anchor='middle' font-size='10' font-weight='bold' font-family='Arial' fill='white'>H</text>`
                    : d.device_type === 'flowerpot'
                    ? `<circle cx='${c}' cy='${c}' r='8' fill='${darkMode ? '#ffa726' : '#E65100'}' stroke='white' stroke-width='1.5'/><text x='${c}' y='${c+4}' text-anchor='middle' font-size='10' font-weight='bold' font-family='Arial' fill='white'>F</text>`
                    : (d.device_type === 'splice_point' || d.device_type === 'splitter_1x4' || d.device_type === 'splitter_2x8')
                    ? `<polygon points='${c-8},${c-4} ${c},${c} ${c-8},${c+4}' fill='${darkMode ? '#ef5350' : '#D32F2F'}' stroke='white' stroke-width='1'/><polygon points='${c+8},${c-4} ${c},${c} ${c+8},${c+4}' fill='${darkMode ? '#ef5350' : '#D32F2F'}' stroke='white' stroke-width='1'/>`
                    : (d.device_type === 'pedestal' || d.device_type === 'cabinet')
                    ? `<rect x='${c-5}' y='${c-7}' width='10' height='14' rx='1' fill='${darkMode ? '#66bb6a' : '#2E7D32'}' stroke='white' stroke-width='1.5'/>`
                    : d.device_type === 'fat'
                    ? `<polygon points='${c},${c-8} ${c+8},${c} ${c},${c+8} ${c-8},${c}' fill='${darkMode ? '#ab47bc' : '#6A1B9A'}' stroke='white' stroke-width='1.5'/><circle cx='${c}' cy='${c}' r='2.5' fill='white'/>`
                    : `<circle cx='${c}' cy='${c}' r='6' fill='#999' stroke='white' stroke-width='1.5'/>`
                  }
                </svg>`;
                iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(beaconSvg)}`;
                iconSize = new window.google.maps.Size(sz, sz);
                iconAnchor = new window.google.maps.Point(sz / 2, sz / 2);
              } else {
                iconUrl = buildDeviceSvg(d.device_type, darkMode);
                iconSize = new window.google.maps.Size(28, 28);
                iconAnchor = new window.google.maps.Point(14, 14);
              }
              return (
                <Marker
                  key={d.device_id}
                  position={{ lat: posLat, lng: posLng }}
                  icon={{ url: iconUrl, scaledSize: iconSize, anchor: iconAnchor }}
                  title={`${d.device_type} — ${d.device_id}${d.status ? ' (' + d.status + ')' : ''}`}
                  zIndex={isSelected ? 900 : 800}
                  onClick={() => {
                    if (placingPin || movingNote) return;
                    if (multiSelectMode) {
                      setSelectedDevices(prev => {
                        const next = new Map(prev);
                        if (next.has(d.device_id)) next.delete(d.device_id);
                        else next.set(d.device_id, d);
                        return next;
                      });
                      return;
                    }
                    setSelectedDevice(d);
                    setSelectedSegment(null);
                    setSelectedNote(null);
                    setStatusPhotos([]);
                  }}
                />
              );
            })}

            {/* GPS user location */}
            {userLocation && (
              <Marker
                position={userLocation}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24'>
                      <circle cx='12' cy='12' r='10' fill='rgba(66,133,244,0.15)' stroke='none'/>
                      <circle cx='12' cy='12' r='6' fill='#4285F4' stroke='white' stroke-width='2'/>
                    </svg>`
                  )}`,
                  scaledSize: new window.google.maps.Size(28, 28),
                  anchor: new window.google.maps.Point(14, 14),
                }}
                title="Your location"
                zIndex={1000}
              />
            )}

            {/* Map notes — uses memoized icon data to prevent darting on mobile */}
            {showNotes && noteMarkerData.map(({ note, lat, lng, icon, zIndex }) => (
              <Marker
                key={note.note_id}
                position={{ lat, lng }}
                icon={icon}
                onClick={() => {
                  if (placingPin || movingNote) return; // don't open popup during place/move
                  markNoteSeen(note.note_id, parseInt(note.reply_count, 10) || 0);
                  setSelectedNote(note);
                  setEditingNote(null);
                  setNoteReplies([]);
                  setCommentText('');
                  loadNoteReplies(note.note_id).then(r => setNoteReplies(r || []));
                }}
                zIndex={zIndex}
              />
            ))}

            {/* Temporary marker at tapped location */}
            {newPinLocation && (
              <Marker
                position={newPinLocation}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    `<svg xmlns='http://www.w3.org/2000/svg' width='38' height='50' viewBox='0 0 24 32'><path d='M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z' fill='#1565c0' stroke='white' stroke-width='2'/><text x='12' y='15' text-anchor='middle' fill='white' font-size='14' font-weight='bold' font-family='sans-serif'>+</text></svg>`
                  )}`,
                  scaledSize: new window.google.maps.Size(38, 50),
                  anchor: new window.google.maps.Point(19, 50),
                }}
                zIndex={999}
              />
            )}

            {/* Note popup */}
            {showNotes && selectedNote && (() => {
              const STATUS_FLOW = {
                open: [{ value: 'in_progress', label: 'Start Work' }],
                assigned: [{ value: 'in_progress', label: 'Start Work' }],
                in_progress: [{ value: 'complete', label: 'Mark Complete' }],
                complete: [{ value: 'verified', label: 'Verify' }],
                verified: [],
              };
              const STATUS_META = {
                open: { color: '#e53935', bg: '#fef2f2', label: 'Open' },
                assigned: { color: '#f57c00', bg: '#fff8e1', label: 'Assigned' },
                in_progress: { color: '#1976d2', bg: '#e3f2fd', label: 'In Progress' },
                complete: { color: '#00897b', bg: '#e0f2f1', label: 'Complete' },
                verified: { color: '#43a047', bg: '#e8f5e9', label: 'Verified' },
              };
              const currentStatus = selectedNote.status || 'open';
              const meta = STATUS_META[currentStatus] || STATUS_META.open;
              const nextActions = STATUS_FLOW[currentStatus] || [];
              const preset = selectedNote.pin_style && getPreset(selectedNote.pin_style);
              const createdAt = selectedNote.created_at ? new Date(selectedNote.created_at) : null;
              const deadline = selectedNote.deadline ? new Date(selectedNote.deadline) : null;
              const isOverdue = deadline && deadline < new Date() && currentStatus !== 'complete' && currentStatus !== 'verified';

              const saveIdentity = () => {
                try { localStorage.setItem('lyt-commenter-name', commentName.trim()); localStorage.setItem('lyt-commenter-phone', commentPhone.trim()); } catch {}
              };

              const handleStatusChange = async (newStatus) => {
                if (!hasIdentity) { setIdentityError(true); return; }
                setIdentityError(false);
                saveIdentity();
                setStatusLoading(true);

                const payload = {
                  note_id: selectedNote.note_id,
                  action: 'transition',
                  status: newStatus,
                  actor_email: commentPhone.trim(),
                  actor_name: commentName.trim(),
                };

                const queueFallback = () => {
                  enqueueAction('transition', payload);
                  setPendingCount(getQueueCount());
                  // Optimistic update
                  setMapNotes(prev => prev.map(n =>
                    n.note_id === selectedNote.note_id ? { ...n, status: newStatus, _pending: true } : n
                  ));
                  setSelectedNote(null);
                  setSyncToast({ type: 'amber', message: 'Status change saved offline' });
                };

                if (!isOnline) {
                  queueFallback();
                  setStatusLoading(false);
                  return;
                }

                try {
                  const result = await transitionMapNote(selectedNote.note_id, newStatus, {
                    actor_email: commentPhone.trim(),
                    actor_name: commentName.trim(),
                  });
                  if (result.success) {
                    setSelectedNote(null);
                    loadData(projectId, selectedSection, true);
                  }
                } catch (err) {
                  console.error('[SharedMap] Status change failed, queuing offline:', err);
                  queueFallback();
                } finally {
                  setStatusLoading(false);
                }
              };

              const handleAddComment = async () => {
                if (!hasIdentity) { setIdentityError(true); return; }
                if (!commentText.trim()) return;
                setIdentityError(false);
                saveIdentity();
                setCommentSaving(true);

                const replyId = `RPL-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
                const replyPayload = {
                  reply_id: replyId,
                  note_id: selectedNote.note_id,
                  text: commentText.trim(),
                  author_name: commentName.trim(),
                  author_phone: commentPhone.trim(),
                  author_role: 'external',
                };

                const queueFallback = () => {
                  enqueueAction('reply', replyPayload);
                  setPendingCount(getQueueCount());
                  // Optimistic append
                  const optimistic = {
                    reply_id: replyId,
                    text: commentText.trim(),
                    author_name: commentName.trim(),
                    created_at: new Date().toISOString(),
                    _pending: true,
                  };
                  setNoteReplies(prev => [...prev, optimistic]);
                  setCommentText('');
                  setSyncToast({ type: 'amber', message: 'Comment saved offline' });
                };

                if (!isOnline) {
                  queueFallback();
                  setCommentSaving(false);
                  return;
                }

                try {
                  const result = await createNoteReply({
                    note_id: selectedNote.note_id,
                    text: commentText.trim(),
                    author_name: commentName.trim(),
                    author_phone: commentPhone.trim(),
                    author_role: 'external',
                  });
                  if (result.success) {
                    const updated = [...noteReplies, result.data];
                    setNoteReplies(updated);
                    setCommentText('');
                    markNoteSeen(selectedNote.note_id, updated.length);
                  } else {
                    console.error('[SharedMap] Comment API error, queuing offline:', result.error);
                    queueFallback();
                  }
                } catch (err) {
                  console.error('[SharedMap] Comment failed, queuing offline:', err);
                  queueFallback();
                } finally {
                  setCommentSaving(false);
                }
              };

              const closePopup = () => { setSelectedNote(null); setIdentityError(false); setEditingNote(null); setStagedPhotos([]); setUploadResult(null); };
              const m = isMobile;
              const ownPin = isOwnNote(selectedNote);

              const popupBody = (
                <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: darkMode ? tc.text : '#1a1a1a' }}>

                  {/* Header: status pill + category + preset + edit/move buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: m ? 12 : 10, flexWrap: 'wrap', paddingRight: m ? 44 : 0 }}>
                    <span style={{
                      padding: m ? '5px 12px' : '3px 10px', borderRadius: 12, fontSize: m ? 12 : 11, fontWeight: 600, letterSpacing: 0.2,
                      color: meta.color, background: meta.bg,
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: m ? 12 : 11, color: darkMode ? tc.textFaint : '#999', textTransform: 'capitalize' }}>{selectedNote.category || 'general'}</span>
                    {preset && (
                      <span style={{
                        padding: m ? '5px 12px' : '3px 10px', borderRadius: 12, fontSize: m ? 12 : 11, fontWeight: 600,
                        color: '#fff', background: preset.color,
                      }}>
                        {preset.label}
                      </span>
                    )}
                    {ownPin && !editingNote && (
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                        <button onClick={() => startEdit(selectedNote)} style={{
                          padding: m ? '8px 16px' : '3px 8px', borderRadius: 6,
                          border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                          background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555',
                          cursor: 'pointer', fontSize: m ? 12 : 11, fontWeight: 500, minHeight: m ? 44 : undefined,
                        }}>Edit</button>
                        <button onClick={() => startMove(selectedNote)} style={{
                          padding: m ? '8px 16px' : '3px 8px', borderRadius: 6,
                          border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                          background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555',
                          cursor: 'pointer', fontSize: m ? 12 : 11, fontWeight: 500, minHeight: m ? 44 : undefined,
                        }}>Move</button>
                      </div>
                    )}
                  </div>

                  {/* Note body — editable if editing own pin */}
                  {editingNote && editingNote.note_id === selectedNote.note_id ? (
                    <div style={{ marginBottom: m ? 12 : 10 }}>
                      <textarea
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        style={{
                          width: '100%', padding: m ? '10px 12px' : '8px 10px', fontSize: m ? 15 : 14,
                          borderRadius: 8, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, outline: 'none', resize: 'vertical',
                          minHeight: m ? 80 : 60, boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5,
                          background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                        <select
                          value={editCategory}
                          onChange={e => setEditCategory(e.target.value)}
                          style={{
                            padding: m ? '8px 10px' : '5px 8px', fontSize: m ? 14 : 12, borderRadius: 6,
                            border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                            background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#333', cursor: 'pointer',
                          }}
                        >
                          {PIN_CATEGORIES.map(cat => (
                            <option key={cat.key} value={cat.key}>{cat.letter} — {cat.label}</option>
                          ))}
                        </select>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                          <button onClick={() => setEditingNote(null)} style={{
                            padding: m ? '8px 14px' : '6px 12px', borderRadius: 6,
                            border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                            background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555',
                            cursor: 'pointer', fontSize: m ? 13 : 12,
                          }}>Cancel</button>
                          <button
                            onClick={handleEditPin}
                            disabled={editSaving || !editText.trim()}
                            style={{
                              padding: m ? '8px 14px' : '6px 12px', borderRadius: 6, border: 'none',
                              background: editSaving || !editText.trim() ? (darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0') : accentColor,
                              color: editSaving || !editText.trim() ? mutedColor : '#fff',
                              cursor: editSaving || !editText.trim() ? 'default' : 'pointer',
                              fontSize: m ? 13 : 12, fontWeight: 600,
                            }}
                          >{editSaving ? 'Saving...' : 'Save'}</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: m ? 15 : 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: m ? 10 : 8, color: darkMode ? tc.text : '#1a1a1a' }}>
                      {selectedNote.text}
                    </div>
                  )}

                  {/* Meta line: author + date */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: m ? 10 : 8, fontSize: m ? 12 : 11, color: darkMode ? tc.textFaint : '#999', flexWrap: 'wrap' }}>
                    {selectedNote.author_name && <span style={{ fontWeight: 500 }}>{selectedNote.author_name}</span>}
                    {createdAt && <span>{createdAt.toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>}
                    {isOverdue && <span style={{ color: '#e53935', fontWeight: 600 }}>Overdue</span>}
                    {deadline && !isOverdue && (
                      <span>Due {deadline.toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                    )}
                  </div>

                  {selectedNote.completion_notes && (
                    <div style={{ fontSize: m ? 13 : 12, color: darkMode ? tc.textMuted : '#555', background: darkMode ? tc.cardBgAlt : '#f8f9fa', borderRadius: 6, padding: m ? '10px 12px' : '8px 10px', marginBottom: m ? 12 : 10, lineHeight: 1.4 }}>
                      {selectedNote.completion_notes}
                    </div>
                  )}

                  {/* Photos — display with download */}
                  {(() => {
                    const photos = selectedNote.photos
                      ? (typeof selectedNote.photos === 'string' ? (() => { try { return JSON.parse(selectedNote.photos); } catch { return []; } })() : selectedNote.photos)
                      : [];
                    return photos.length > 0 && (
                      <div style={{ marginBottom: m ? 12 : 10 }}>
                        <div style={{ fontSize: m ? 13 : 12, fontWeight: 600, color: darkMode ? tc.textMuted : '#444', marginBottom: 6 }}>
                          Photos ({photos.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: m ? 8 : 6 }}>
                          {photos.map((url, i) => (
                            <div key={i} style={{ position: 'relative', width: m ? 72 : 56, height: m ? 72 : 56 }}>
                              <img
                                src={url}
                                alt={`Photo ${i + 1}`}
                                onClick={() => window.open(url, '_blank')}
                                style={{
                                  width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6,
                                  cursor: 'pointer', border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                                }}
                              />
                              <a
                                href={url}
                                download={`pin-photo-${i + 1}.jpg`}
                                onClick={e => e.stopPropagation()}
                                style={{
                                  position: 'absolute', bottom: 2, right: 2,
                                  width: m ? 32 : 20, height: m ? 32 : 20, borderRadius: '50%',
                                  background: 'rgba(0,0,0,0.6)', color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  textDecoration: 'none',
                                }}
                                title="Download"
                              >
                                <Download size={m ? 16 : 10} />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Add photo to existing pin */}
                  {ownPin && selectedNote.status !== 'verified' && !editingNote && (
                    <div style={{ marginBottom: m ? 12 : 10 }}>
                      <input
                        type="file"
                        ref={existingPhotoRef}
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={handleExistingPinPhoto}
                        style={srOnly}
                      />
                      <input
                        type="file"
                        ref={existingGalleryRef}
                        accept="image/*"
                        multiple
                        onChange={handleExistingPinPhoto}
                        style={srOnly}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => existingPhotoRef.current?.click()}
                          disabled={photoUploading}
                          style={{
                            padding: m ? '8px 14px' : '5px 10px', borderRadius: 6,
                            border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                            background: darkMode ? tc.cardBgAlt : '#fff',
                            color: photoUploading ? mutedColor : (darkMode ? tc.textMuted : '#555'),
                            cursor: photoUploading ? 'default' : 'pointer',
                            fontSize: m ? 13 : 12, fontWeight: 500,
                            minHeight: m ? 44 : undefined,
                          }}
                        >
                          {photoUploading ? 'Uploading...' : '📷 Camera'}
                        </button>
                        <button
                          type="button"
                          onClick={() => existingGalleryRef.current?.click()}
                          disabled={photoUploading}
                          style={{
                            padding: m ? '8px 14px' : '5px 10px', borderRadius: 6,
                            border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                            background: darkMode ? tc.cardBgAlt : '#fff',
                            color: photoUploading ? mutedColor : (darkMode ? tc.textMuted : '#555'),
                            cursor: photoUploading ? 'default' : 'pointer',
                            fontSize: m ? 13 : 12, fontWeight: 500,
                            minHeight: m ? 44 : undefined,
                          }}
                        >
                          {photoUploading ? 'Uploading...' : '📁 Upload'}
                        </button>
                      </div>
                      {/* Staged photo previews + confirm button */}
                      {stagedPhotos.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {stagedPhotos.map((file, i) => (
                              <div key={i} style={{ position: 'relative', width: m ? 56 : 44, height: m ? 56 : 44 }}>
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}` }}
                                />
                                <button
                                  onClick={() => setStagedPhotos(prev => prev.filter((_, idx) => idx !== i))}
                                  style={{
                                    position: 'absolute', top: -4, right: -4,
                                    width: 20, height: 20, borderRadius: '50%',
                                    background: '#F44336', color: '#fff', border: 'none',
                                    fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    lineHeight: 1, padding: 0,
                                  }}
                                >x</button>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={handleConfirmUpload}
                              disabled={photoUploading}
                              style={{
                                padding: m ? '10px 20px' : '6px 14px', borderRadius: 8,
                                border: 'none', background: '#4CAF50', color: '#fff',
                                cursor: photoUploading ? 'default' : 'pointer',
                                fontSize: m ? 14 : 12, fontWeight: 600,
                                minHeight: m ? 44 : undefined,
                                opacity: photoUploading ? 0.7 : 1,
                              }}
                            >
                              {photoUploading ? 'Uploading...' : `Submit ${stagedPhotos.length} Photo${stagedPhotos.length > 1 ? 's' : ''}`}
                            </button>
                            <button
                              onClick={() => setStagedPhotos([])}
                              disabled={photoUploading}
                              style={{
                                padding: m ? '10px 14px' : '6px 10px', borderRadius: 8,
                                border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                                background: 'transparent', color: mutedColor,
                                cursor: photoUploading ? 'default' : 'pointer',
                                fontSize: m ? 14 : 12, fontWeight: 500,
                                minHeight: m ? 44 : undefined,
                              }}
                            >Cancel</button>
                          </div>
                        </div>
                      )}
                      {/* Upload result feedback */}
                      {uploadResult && (
                        <div style={{
                          marginTop: 8, padding: m ? '8px 12px' : '6px 10px', borderRadius: 8,
                          background: uploadResult.type === 'success' ? (darkMode ? 'rgba(76,175,80,0.15)' : '#e8f5e9')
                            : uploadResult.type === 'partial' ? (darkMode ? 'rgba(255,152,0,0.15)' : '#fff3e0')
                            : (darkMode ? 'rgba(244,67,54,0.15)' : '#ffebee'),
                          color: uploadResult.type === 'success' ? '#4CAF50'
                            : uploadResult.type === 'partial' ? '#FF9800'
                            : '#F44336',
                          fontSize: m ? 13 : 12, fontWeight: 600,
                        }}>
                          {uploadResult.type === 'success' && `✓ ${uploadResult.count} photo${uploadResult.count > 1 ? 's' : ''} uploaded`}
                          {uploadResult.type === 'partial' && `⚠ ${uploadResult.count}/${uploadResult.total} photos uploaded`}
                          {uploadResult.type === 'error' && '✗ Upload failed — try a smaller image'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Thread / comments */}
                  {noteReplies.length > 0 && (
                    <div style={{ borderTop: `1px solid ${darkMode ? tc.borderSubtle : '#eee'}`, paddingTop: m ? 12 : 10, marginBottom: m ? 12 : 10 }}>
                      <div style={{ fontSize: m ? 13 : 12, fontWeight: 600, color: darkMode ? tc.textMuted : '#444', marginBottom: 8 }}>
                        Thread ({noteReplies.length})
                      </div>
                      <div style={{ maxHeight: m ? 220 : 160, overflowY: 'auto' }}>
                        {noteReplies.map(r => (
                          <div key={r.reply_id} style={{ display: 'flex', gap: 8, marginBottom: m ? 12 : 10 }}>
                            <div style={{
                              width: m ? 32 : 28, height: m ? 32 : 28, borderRadius: '50%', flexShrink: 0,
                              background: darkMode ? 'rgba(102,126,234,0.2)' : '#e3f2fd',
                              color: darkMode ? tc.accentPrimary : '#1565c0',
                              fontSize: m ? 13 : 12, fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {(r.author_name || '?')[0].toUpperCase()}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: m ? 13 : 12, fontWeight: 600, color: darkMode ? tc.text : '#333' }}>{r.author_name || 'Unknown'}</span>
                                <span style={{ fontSize: m ? 11 : 10, color: darkMode ? tc.textFaint : '#bbb' }}>
                                  {r.created_at ? new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                              <div style={{ fontSize: m ? 14 : 13, color: darkMode ? tc.textMuted : '#444', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                                {r.text}
                                {r._pending && <span style={{ fontSize: m ? 11 : 10, color: '#f59e0b', fontStyle: 'italic', marginLeft: 6 }}>(pending sync)</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action area */}
                  <div style={{ borderTop: `1px solid ${darkMode ? tc.borderSubtle : '#eee'}`, paddingTop: m ? 12 : 10 }}>

                    {/* Identity: show compact label if set, inputs if not */}
                    {hasIdentity ? (
                      <div style={{ fontSize: m ? 12 : 11, color: darkMode ? tc.textFaint : '#999', marginBottom: m ? 10 : 8 }}>
                        Commenting as <span style={{ fontWeight: 600, color: darkMode ? tc.textMuted : '#555' }}>{commentName.trim()}</span>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexDirection: m ? 'column' : 'row', gap: 6, marginBottom: 6 }}>
                          <input
                            placeholder="Your name"
                            value={commentName}
                            onChange={e => { setCommentName(e.target.value); setIdentityError(false); }}
                            style={{
                              flex: 1, padding: m ? '10px 12px' : '7px 10px', fontSize: m ? 16 : 13, borderRadius: 6, boxSizing: 'border-box',
                              border: `1px solid ${identityError && !commentName.trim() ? '#e53935' : (darkMode ? tc.border : '#ddd')}`,
                              outline: 'none', minHeight: m ? 44 : undefined,
                              background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a',
                            }}
                          />
                          <input
                            placeholder="Phone"
                            value={commentPhone}
                            onChange={e => { setCommentPhone(e.target.value); setIdentityError(false); }}
                            type="tel"
                            style={{
                              flex: 1, padding: m ? '10px 12px' : '7px 10px', fontSize: m ? 16 : 13, borderRadius: 6, boxSizing: 'border-box',
                              border: `1px solid ${identityError && !commentPhone.trim() ? '#e53935' : (darkMode ? tc.border : '#ddd')}`,
                              outline: 'none', minHeight: m ? 44 : undefined,
                              background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a',
                            }}
                          />
                        </div>
                        {identityError && <div style={{ fontSize: m ? 12 : 11, color: tc.error, marginBottom: 6 }}>Name and phone required</div>}
                      </>
                    )}

                    {/* Comment input + send */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: nextActions.length > 0 ? (m ? 10 : 8) : 0 }}>
                      <input
                        ref={commentInputRef}
                        placeholder="Add a comment..."
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                        onFocus={() => { if (m) setTimeout(() => { commentInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 350); }}
                        style={{
                          flex: 1, padding: m ? '10px 12px' : '7px 10px', fontSize: m ? 16 : 13, borderRadius: 6, boxSizing: 'border-box',
                          border: `1px solid ${darkMode ? tc.border : '#ddd'}`, outline: 'none', minHeight: m ? 44 : undefined,
                          background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a',
                        }}
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={commentSaving || !commentText.trim()}
                        style={{
                          padding: m ? '10px 16px' : '7px 14px', fontSize: m ? 14 : 12, fontWeight: 600, borderRadius: 6, border: 'none',
                          cursor: commentSaving || !commentText.trim() ? 'default' : 'pointer',
                          background: commentSaving || !commentText.trim() ? (darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0') : accentColor,
                          color: commentSaving || !commentText.trim() ? mutedColor : '#fff',
                          transition: 'all 0.15s', whiteSpace: 'nowrap', minHeight: m ? 44 : undefined,
                        }}
                      >
                        {commentSaving ? '...' : 'Send'}
                      </button>
                    </div>

                    {/* Next status action */}
                    {nextActions.length > 0 && (
                      <div>
                        {nextActions.map(action => (
                          <button
                            key={action.value}
                            onClick={() => handleStatusChange(action.value)}
                            disabled={statusLoading}
                            style={{
                              width: '100%', padding: m ? '12px 0' : '9px 0', fontSize: m ? 15 : 13, fontWeight: 600, borderRadius: 8,
                              border: 'none', cursor: statusLoading ? 'default' : 'pointer',
                              background: statusLoading ? (darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0') : (STATUS_META[action.value] || STATUS_META.open).color,
                              color: '#fff', transition: 'opacity 0.15s',
                              opacity: statusLoading ? 0.6 : 1, minHeight: m ? 48 : undefined,
                            }}
                          >
                            {statusLoading ? 'Updating...' : action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );

              // Mobile: bottom sheet instead of InfoWindow
              if (isMobile) {
                return (
                  <>
                    <div onClick={closePopup} style={{
                      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
                      background: 'rgba(0,0,0,0.4)',
                    }} />
                    <div style={{
                      position: 'fixed', bottom: kbHeight, left: 0, right: 0, zIndex: 1001,
                      background: darkMode ? 'rgba(30,41,59,0.92)' : 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                      borderRadius: '20px 20px 0 0',
                      boxShadow: '0 -8px 32px rgba(0,0,0,0.25)',
                      maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight}px)` : '75vh',
                      paddingBottom: kbHeight > 0 ? 0 : 'env(safe-area-inset-bottom, 0px)',
                      transition: 'bottom 0.15s ease-out, max-height 0.15s ease-out',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                        <div style={{ width: 36, height: 4, borderRadius: 2, background: mutedColor, opacity: 0.4 }} />
                      </div>
                      <button onClick={closePopup} style={{
                        position: 'absolute', top: 8, right: 12, background: 'none', border: 'none',
                        fontSize: 24, color: mutedColor, cursor: 'pointer', padding: 6, lineHeight: 1,
                        minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>×</button>
                      <div style={{ overflowY: 'auto', maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 44}px)` : 'calc(75vh - 44px)', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
                        {popupBody}
                      </div>
                    </div>
                  </>
                );
              }

              // Desktop: InfoWindow
              return (
              <InfoWindow
                position={{ lat: parseFloat(selectedNote.lat), lng: parseFloat(selectedNote.lng) }}
                onCloseClick={closePopup}
                options={{ maxWidth: 380 }}
              >
                {darkMode ? (
                  <div style={{ background: tc.cardBg, margin: -8, padding: 12, borderRadius: 8 }}>
                    {popupBody}
                  </div>
                ) : popupBody}
              </InfoWindow>
              );
            })()}

            {/* Desktop status InfoWindow for selected segment/device */}
            {!isMobile && !multiSelectMode && statusSheetItem && (() => {
              const STATUS_BUTTONS = [
                { value: 'In Progress', label: 'In Progress', color: '#FFB800' },
                { value: 'Complete', label: 'Complete', color: '#4CAF50' },
                { value: 'Issue', label: 'Issue', color: '#FF5722' },
              ];
              if (qcMode) STATUS_BUTTONS.push({ value: 'QC Approved', label: 'QC Approved', color: '#2196F3' });

              const statusBody = (
                <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: darkMode ? tc.text : '#1a1a1a' }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{statusSheetItemId}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                      {statusSheetIsSegment && statusSheetItem.street && <span style={{ fontSize: 12, color: mutedColor }}>{statusSheetItem.street}</span>}
                      {statusSheetItem.section && <span style={{ fontSize: 11, color: mutedColor, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>{statusSheetItem.section}</span>}
                      {statusSheetIsSegment && statusSheetItem.footage && <span style={{ fontSize: 11, fontWeight: 600 }}>{statusSheetItem.footage} ft</span>}
                      {!statusSheetIsSegment && <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>{(statusSheetItem.device_type || '').replace(/_/g, ' ')}</span>}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 700, letterSpacing: 0.2, color: '#fff', background: getStatusColor(statusSheetCurrentStatus) }}>{statusSheetCurrentStatus}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {STATUS_BUTTONS.map(btn => {
                      const isDisabled = statusSheetCurrentStatus === btn.value || statusUpdating;
                      return (
                        <button key={btn.value} onClick={() => !isDisabled && handleItemStatusChange(btn.value)} disabled={isDisabled} style={{
                          flex: '1 1 auto', minWidth: 70, minHeight: 36, padding: '6px 10px', borderRadius: 8,
                          border: statusSheetCurrentStatus === btn.value ? `2px solid ${btn.color}` : `1px solid ${darkMode ? tc.border : '#ddd'}`,
                          background: statusSheetCurrentStatus === btn.value ? (darkMode ? 'rgba(255,255,255,0.06)' : '#f5f5f5') : 'transparent',
                          color: isDisabled ? mutedColor : btn.color, cursor: isDisabled ? 'default' : 'pointer',
                          fontSize: 12, fontWeight: 600, opacity: statusUpdating ? 0.6 : 1, transition: 'all 0.15s',
                        }}>{statusUpdating && !isDisabled ? '...' : btn.label}</button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    <input type="file" ref={statusPhotoRef} accept="image/*" capture="environment" multiple onChange={handleStatusPhotoCapture} style={srOnly} />
                    <input type="file" ref={statusGalleryRef} accept="image/*" multiple onChange={handleStatusPhotoCapture} style={srOnly} />
                    <button onClick={() => statusPhotoRef.current?.click()} disabled={statusPhotoUploading} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, background: darkMode ? tc.cardBgAlt : '#fff', color: statusPhotoUploading ? mutedColor : (darkMode ? tc.textMuted : '#555'), cursor: statusPhotoUploading ? 'default' : 'pointer', fontSize: 12, fontWeight: 500 }}>{statusPhotoUploading ? 'Uploading...' : 'Camera'}</button>
                    <button onClick={() => statusGalleryRef.current?.click()} disabled={statusPhotoUploading} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, background: darkMode ? tc.cardBgAlt : '#fff', color: statusPhotoUploading ? mutedColor : (darkMode ? tc.textMuted : '#555'), cursor: statusPhotoUploading ? 'default' : 'pointer', fontSize: 12, fontWeight: 500 }}>{statusPhotoUploading ? 'Uploading...' : 'Upload'}</button>
                    {hasIdentity && (
                      <button onClick={() => {
                        if (statusSheetIsSegment) { setLinkSegmentId(statusSheetItem.segment_id); } else { setLinkDeviceId(statusSheetItem.device_id); }
                        setNewPinLocation({ lat: statusSheetLat, lng: statusSheetLng });
                        setSelectedSegment(null); setSelectedDevice(null);
                      }} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 500, marginLeft: 'auto' }}>Add Note</button>
                    )}
                  </div>
                  {statusPhotos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {statusPhotos.map((file, i) => (
                        <div key={i} style={{ position: 'relative', width: 44, height: 44 }}>
                          <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}` }} />
                          <button onClick={() => setStatusPhotos(prev => prev.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, borderRadius: '50%', background: '#F44336', color: '#fff', border: 'none', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>x</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {statusSheetLinkedNotes.length > 0 && (
                    <div style={{ borderTop: `1px solid ${darkMode ? tc.borderSubtle : '#eee'}`, paddingTop: 8, marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: darkMode ? tc.textMuted : '#444', marginBottom: 6 }}>Linked Notes ({statusSheetLinkedNotes.length})</div>
                      {statusSheetLinkedNotes.map(note => {
                        const catInfo = PIN_CAT_MAP[note.category] || PIN_CAT_MAP.general;
                        return (
                          <div key={note.note_id} onClick={() => { setSelectedSegment(null); setSelectedDevice(null); setSelectedNote(note); setNoteReplies([]); loadNoteReplies(note.note_id).then(r => setNoteReplies(r || [])); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? tc.borderSubtle : '#f0f0f0'}` }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: catInfo.color, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.text}</span>
                            <span style={{ fontSize: 10, color: mutedColor, flexShrink: 0 }}>{note.status || 'open'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {hasIdentity ? (
                    <div style={{ fontSize: 11, color: darkMode ? tc.textFaint : '#999' }}>Updating as <span style={{ fontWeight: 600, color: darkMode ? tc.textMuted : '#555' }}>{commentName.trim()}</span></div>
                  ) : (
                    <div style={{ borderTop: `1px solid ${darkMode ? tc.borderSubtle : '#eee'}`, paddingTop: 8 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <input placeholder="Your name" value={commentName} onChange={e => { setCommentName(e.target.value); try { localStorage.setItem('lyt-commenter-name', e.target.value); } catch {} setIdentityError(false); }} style={{ flex: 1, padding: '7px 10px', fontSize: 13, borderRadius: 6, boxSizing: 'border-box', border: `1px solid ${identityError && !commentName.trim() ? '#e53935' : (darkMode ? tc.border : '#ddd')}`, outline: 'none', background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a' }} />
                        <input placeholder="Phone" value={commentPhone} onChange={e => { setCommentPhone(e.target.value); try { localStorage.setItem('lyt-commenter-phone', e.target.value); } catch {} setIdentityError(false); }} type="tel" style={{ flex: 1, padding: '7px 10px', fontSize: 13, borderRadius: 6, boxSizing: 'border-box', border: `1px solid ${identityError && !commentPhone.trim() ? '#e53935' : (darkMode ? tc.border : '#ddd')}`, outline: 'none', background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a' }} />
                      </div>
                      {identityError && <div style={{ fontSize: 11, color: tc.error, marginBottom: 6 }}>Name and phone required to update status</div>}
                    </div>
                  )}
                </div>
              );

              return (
                <InfoWindow
                  position={{ lat: statusSheetLat, lng: statusSheetLng }}
                  onCloseClick={closeStatusSheet}
                  options={{ maxWidth: 400 }}
                >
                  {darkMode ? (
                    <div style={{ background: tc.cardBg, margin: -8, padding: 12, borderRadius: 8 }}>
                      {statusBody}
                    </div>
                  ) : statusBody}
                </InfoWindow>
              );
            })()}
          </GoogleMap>
        )}

        {/* Legend overlay */}
        {showLegend && (
          <div style={{
            position: 'absolute',
            bottom: isMobile ? 70 : 16,
            left: isMobile ? 12 : 16,
            right: isMobile ? 70 : undefined,
            zIndex: 15,
            background: darkMode ? 'rgba(17,34,64,0.95)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            borderRadius: 12, padding: isMobile ? '14px 18px' : '12px 16px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            border: `1px solid ${borderColor}`,
            minWidth: isMobile ? undefined : 160,
          }}>
            <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 12, marginBottom: 8, textTransform: 'capitalize' }}>
              {phase} Status Legend
            </div>
            <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : undefined, flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? '6px 14px' : 4 }}>
              {[
                { label: 'Not Started', color: STATUS_COLORS.NOT_STARTED },
                { label: 'In Progress', color: STATUS_COLORS.IN_PROGRESS },
                { label: 'Complete', color: STATUS_COLORS.COMPLETE },
                { label: 'QC Approved', color: STATUS_COLORS.QC_APPROVED },
                { label: 'Issue', color: STATUS_COLORS.ISSUE },
                { label: 'Blocked', color: STATUS_COLORS.BLOCKED },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 20, height: 5, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                  <span style={{ fontSize: isMobile ? 13 : 11, color: textColor, whiteSpace: 'nowrap' }}>{item.label}</span>
                </div>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${borderColor}`, marginTop: 8, paddingTop: 8 }}>
              <div style={{ fontWeight: 600, fontSize: isMobile ? 13 : 11, marginBottom: 6 }}>Devices</div>
              <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : undefined, flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? '6px 14px' : 3 }}>
                {[
                  { label: 'Handhole', type: 'handhole' },
                  { label: 'Flowerpot', type: 'flowerpot' },
                  { label: 'Splice Point', type: 'splice_point' },
                  { label: 'Pedestal/Cabinet', type: 'pedestal' },
                  { label: 'FAT', type: 'fat' },
                ].map(d => (
                  <div key={d.type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <img src={buildDeviceSvg(d.type, darkMode)} alt="" width={14} height={14} />
                    <span style={{ fontSize: isMobile ? 13 : 11, color: textColor, whiteSpace: 'nowrap' }}>{d.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Offline banner */}
        {!isOnline && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20,
            background: '#f59e0b', color: '#78350f',
            padding: isMobile ? '10px 16px' : '6px 16px', fontSize: isMobile ? 14 : 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <WifiOff size={isMobile ? 16 : 14} />
            <span>Offline — actions will sync when signal returns{pendingCount > 0 ? ` (${pendingCount} pending)` : ''}</span>
          </div>
        )}

        {/* Multi-select mode banner */}
        {multiSelectMode && !batchUpdating && (
          <div style={{
            position: 'absolute', top: !isOnline ? (isMobile ? 48 : 36) : 12, left: '50%', transform: 'translateX(-50%)', zIndex: 25,
            background: darkMode ? 'rgba(0,229,255,0.18)' : 'rgba(0,229,255,0.12)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(0,229,255,0.4)',
            color: darkMode ? '#80deea' : '#006064',
            borderRadius: 20, padding: isMobile ? '8px 16px' : '6px 14px',
            fontSize: isMobile ? 14 : 12, fontWeight: 600,
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'fadeInDown 0.2s ease-out',
            whiteSpace: 'nowrap',
          }}>
            <Layers size={isMobile ? 14 : 12} />
            <span>{multiSelectCount === 0 ? 'Tap items to select' : `${multiSelectCount} selected`}</span>
            {multiSelectCount > 0 && (
              <button onClick={() => { setSelectedSegments(new Map()); setSelectedDevices(new Map()); }} style={{
                background: 'rgba(0,229,255,0.2)', border: 'none', borderRadius: 10,
                color: 'inherit', padding: isMobile ? '6px 14px' : '2px 8px', cursor: 'pointer',
                fontSize: isMobile ? 13 : 11, fontWeight: 600, minHeight: isMobile ? 36 : undefined,
              }}>Clear</button>
            )}
            <button onClick={() => { setMultiSelectMode(false); setSelectedSegments(new Map()); setSelectedDevices(new Map()); }} style={{
              background: 'rgba(0,229,255,0.2)', border: 'none', borderRadius: 10,
              color: 'inherit', padding: isMobile ? '6px 14px' : '2px 8px', cursor: 'pointer',
              fontSize: isMobile ? 13 : 11, fontWeight: 600, minHeight: isMobile ? 36 : undefined,
            }}>Exit</button>
          </div>
        )}

        {/* Sync toast */}
        {syncToast && (
          <div style={{
            position: 'absolute', top: !isOnline ? (isMobile ? 48 : 36) : 12, left: '50%', transform: 'translateX(-50%)', zIndex: 25,
            background: syncToast.type === 'green' ? '#059669' : '#f59e0b',
            color: syncToast.type === 'green' ? '#fff' : '#78350f',
            borderRadius: 20, padding: isMobile ? '8px 20px' : '6px 16px',
            fontSize: isMobile ? 14 : 12, fontWeight: 600,
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            whiteSpace: 'nowrap',
            animation: 'fadeInDown 0.2s ease-out',
          }}>
            {syncToast.message}
          </div>
        )}

        {/* Move-pin mode banner (pin placement uses FAB toggle instead) */}
        {movingNote && (
          <div style={{
            position: 'absolute', top: !isOnline ? (isMobile ? 48 : 36) : 12, left: '50%', transform: 'translateX(-50%)', zIndex: 25,
            background: darkMode ? 'rgba(102,126,234,0.9)' : 'rgba(21,101,192,0.92)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            color: '#fff',
            borderRadius: 20, padding: isMobile ? '10px 20px' : '6px 16px',
            fontSize: isMobile ? 15 : 12, fontWeight: 600,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'fadeInDown 0.2s ease-out',
          }}>
            <MapPin size={isMobile ? 16 : 14} />
            <span>Tap new location for pin</span>
            <button onClick={cancelPinAction} style={{
              marginLeft: 4, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 12,
              color: '#fff', padding: isMobile ? '6px 14px' : '2px 8px', cursor: 'pointer',
              fontSize: isMobile ? 14 : 11, fontWeight: 600, minHeight: isMobile ? 36 : undefined,
            }}>Cancel</button>
          </div>
        )}


        {/* "View only" badge — only when identity not set and not in action mode */}
        {!isMobile && !syncToast && isOnline && !placingPin && !movingNote && !newPinLocation && !hasIdentity && !selectedSegment && !selectedDevice && (
          <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 15,
            background: darkMode ? 'rgba(17,34,64,0.9)' : 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(6px)',
            borderRadius: 20, padding: '5px 14px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: `1px solid ${borderColor}`,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Eye size={12} style={{ color: mutedColor }} />
            <span style={{ fontSize: 11, color: mutedColor, fontWeight: 500 }}>View Only</span>
          </div>
        )}

        {/* Pending actions badge (bottom-left) */}
        {pendingCount > 0 && (
          <div style={{
            position: 'absolute', bottom: isMobile ? 12 : 16, left: isMobile ? 12 : 16, zIndex: 15,
            background: '#f59e0b', color: '#78350f',
            borderRadius: 20, padding: '6px 14px',
            fontSize: 12, fontWeight: 700,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#78350f', animation: 'pulse 2s ease-in-out infinite' }} />
            {pendingCount} pending
          </div>
        )}

        {/* Map action buttons (bottom-right stack) */}
        <div style={{
          position: 'absolute', bottom: (multiSelectMode && multiSelectCount > 0) ? (isMobile ? 120 : 80) : (isMobile ? 12 : 16), right: isMobile ? 12 : 16, zIndex: 15,
          display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 8,
          transition: 'bottom 0.2s ease',
        }}>
          {/* Multi-Select FAB — toggle multi-select mode */}
          {hasIdentity && !placingPin && !movingNote && !newPinLocation && (
            <button
              onClick={() => {
                if (multiSelectMode) {
                  setMultiSelectMode(false);
                  setSelectedSegments(new Map());
                  setSelectedDevices(new Map());
                } else {
                  setMultiSelectMode(true);
                  setSelectedSegment(null);
                  setSelectedDevice(null);
                  setSelectedNote(null);
                }
              }}
              style={{
                width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, borderRadius: 12,
                border: multiSelectMode ? '2px solid #00e5ff' : `1px solid ${borderColor}`,
                background: multiSelectMode
                  ? (darkMode ? 'rgba(0,229,255,0.25)' : 'rgba(0,229,255,0.15)')
                  : (darkMode ? 'rgba(17,34,64,0.9)' : 'rgba(255,255,255,0.9)'),
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                boxShadow: multiSelectMode ? '0 0 12px rgba(0,229,255,0.4)' : '0 2px 8px rgba(0,0,0,0.15)',
                color: multiSelectMode ? '#00e5ff' : mutedColor,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              title={multiSelectMode ? 'Exit multi-select' : 'Multi-select mode'}
            >
              <Layers size={isMobile ? 20 : 16} />
            </button>
          )}

          {/* Add Pin FAB — MapPin icon, toggles to X cancel when in placement mode */}
          {showNotes && hasIdentity && !movingNote && !newPinLocation && !multiSelectMode && (
            <button
              onClick={() => {
                if (placingPin) { setPlacingPin(false); }
                else { setPlacingPin(true); setSelectedNote(null); setSelectedSegment(null); setSelectedDevice(null); }
              }}
              style={{
                width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, borderRadius: 12,
                border: placingPin ? '2px solid rgba(255,255,255,0.5)' : 'none',
                background: placingPin ? '#e53935' : accentColor,
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                boxShadow: placingPin ? '0 0 12px rgba(229,57,53,0.5)' : `0 4px 16px ${accentColor}55`,
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              title={placingPin ? 'Cancel pin placement' : 'Add a pin'}
            >
              {placingPin ? <Plus size={isMobile ? 22 : 18} style={{ transform: 'rotate(45deg)' }} /> : <MapPin size={isMobile ? 22 : 18} />}
            </button>
          )}

          {/* GPS locate / follow toggle */}
          <button
            onClick={locateMe}
            disabled={locating}
            style={{
              width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, borderRadius: 12,
              border: gpsFollowing ? '2px solid #4285F4' : `1px solid ${borderColor}`,
              background: gpsFollowing
                ? (darkMode ? 'rgba(66,133,244,0.35)' : 'rgba(66,133,244,0.2)')
                : userLocation
                  ? (darkMode ? 'rgba(66,133,244,0.2)' : 'rgba(66,133,244,0.1)')
                  : (darkMode ? 'rgba(17,34,64,0.9)' : 'rgba(255,255,255,0.9)'),
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              boxShadow: gpsFollowing ? '0 0 8px rgba(66,133,244,0.4)' : '0 2px 8px rgba(0,0,0,0.15)',
              color: gpsFollowing || userLocation ? '#4285F4' : mutedColor,
              cursor: locating ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            title={gpsFollowing ? 'Stop following' : 'Follow my location'}
          >
            <LocateFixed size={isMobile ? 20 : 16} style={locating ? { animation: 'spin 1s linear infinite' } : undefined} />
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            style={{
              width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, borderRadius: 12,
              border: `1px solid ${borderColor}`,
              background: darkMode ? 'rgba(17,34,64,0.9)' : 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              color: mutedColor, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={isMobile ? 20 : 16} /> : <Maximize2 size={isMobile ? 20 : 16} />}
          </button>
        </div>
      </div>

      {/* Segment/Device status bottom sheet — MOBILE ONLY (desktop InfoWindow is inside GoogleMap) */}
      {isMobile && !multiSelectMode && statusSheetItem && (() => {
        const STATUS_BUTTONS = [
          { value: 'In Progress', label: 'In Progress', color: '#FFB800' },
          { value: 'Complete', label: 'Complete', color: '#4CAF50' },
          { value: 'Issue', label: 'Issue', color: '#FF5722' },
        ];
        if (qcMode) STATUS_BUTTONS.push({ value: 'QC Approved', label: 'QC Approved', color: '#2196F3' });
        const m = true;

        return (
          <>
            <div onClick={closeStatusSheet} style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
              background: 'rgba(0,0,0,0.4)',
            }} />
            <div style={{
              position: 'fixed', bottom: kbHeight, left: 0, right: 0, zIndex: 1001,
              background: darkMode ? 'rgba(30,41,59,0.92)' : 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.25)',
              maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight}px)` : '75vh',
              paddingBottom: kbHeight > 0 ? 0 : 'env(safe-area-inset-bottom, 0px)',
              transition: 'bottom 0.15s ease-out, max-height 0.15s ease-out',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: mutedColor, opacity: 0.4 }} />
              </div>
              <button onClick={closeStatusSheet} style={{
                position: 'absolute', top: 8, right: 12, background: 'none', border: 'none',
                fontSize: 24, color: mutedColor, cursor: 'pointer', padding: 6, lineHeight: 1,
                minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>x</button>
              <div style={{ overflowY: 'auto', maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 44}px)` : 'calc(75vh - 44px)', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: darkMode ? tc.text : '#1a1a1a' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>{statusSheetItemId}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                      {statusSheetIsSegment && statusSheetItem.street && <span style={{ fontSize: 13, color: mutedColor }}>{statusSheetItem.street}</span>}
                      {statusSheetItem.section && <span style={{ fontSize: 12, color: mutedColor, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>{statusSheetItem.section}</span>}
                      {statusSheetIsSegment && statusSheetItem.footage && <span style={{ fontSize: 12, fontWeight: 600 }}>{statusSheetItem.footage} ft</span>}
                      {!statusSheetIsSegment && <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>{(statusSheetItem.device_type || '').replace(/_/g, ' ')}</span>}
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 14, fontSize: 13, fontWeight: 700, letterSpacing: 0.2, color: '#fff', background: getStatusColor(statusSheetCurrentStatus) }}>{statusSheetCurrentStatus}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                    {STATUS_BUTTONS.map(btn => {
                      const isDisabled = statusSheetCurrentStatus === btn.value || statusUpdating;
                      return (
                        <button key={btn.value} onClick={() => !isDisabled && handleItemStatusChange(btn.value)} disabled={isDisabled} style={{
                          flex: '1 1 auto', minWidth: 90, minHeight: 48, padding: '10px 8px', borderRadius: 8,
                          border: statusSheetCurrentStatus === btn.value ? `2px solid ${btn.color}` : `1px solid ${darkMode ? tc.border : '#ddd'}`,
                          background: statusSheetCurrentStatus === btn.value ? (darkMode ? 'rgba(255,255,255,0.06)' : '#f5f5f5') : 'transparent',
                          color: isDisabled ? mutedColor : btn.color, cursor: isDisabled ? 'default' : 'pointer',
                          fontSize: 13, fontWeight: 600, opacity: statusUpdating ? 0.6 : 1, transition: 'all 0.15s',
                        }}>{statusUpdating && !isDisabled ? '...' : btn.label}</button>
                      );
                    })}
                  </div>
                  <input type="file" ref={statusPhotoRef} accept="image/*" capture="environment" multiple onChange={handleStatusPhotoCapture} style={srOnly} />
                  <input type="file" ref={statusGalleryRef} accept="image/*" multiple onChange={handleStatusPhotoCapture} style={srOnly} />
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <button onClick={() => statusPhotoRef.current?.click()} disabled={statusPhotoUploading} style={{ padding: '8px 14px', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, background: darkMode ? tc.cardBgAlt : '#fff', color: statusPhotoUploading ? mutedColor : (darkMode ? tc.textMuted : '#555'), cursor: statusPhotoUploading ? 'default' : 'pointer', fontSize: 13, fontWeight: 500, minHeight: 44 }}>{statusPhotoUploading ? 'Uploading...' : 'Camera'}</button>
                    <button onClick={() => statusGalleryRef.current?.click()} disabled={statusPhotoUploading} style={{ padding: '8px 14px', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, background: darkMode ? tc.cardBgAlt : '#fff', color: statusPhotoUploading ? mutedColor : (darkMode ? tc.textMuted : '#555'), cursor: statusPhotoUploading ? 'default' : 'pointer', fontSize: 13, fontWeight: 500, minHeight: 44 }}>{statusPhotoUploading ? 'Uploading...' : 'Upload'}</button>
                    {hasIdentity && (
                      <button onClick={() => {
                        if (statusSheetIsSegment) { setLinkSegmentId(statusSheetItem.segment_id); } else { setLinkDeviceId(statusSheetItem.device_id); }
                        setNewPinLocation({ lat: statusSheetLat, lng: statusSheetLng });
                        setSelectedSegment(null); setSelectedDevice(null);
                      }} style={{ padding: '8px 14px', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555', cursor: 'pointer', fontSize: 13, fontWeight: 500, minHeight: 44, marginLeft: 'auto' }}>Add Note</button>
                    )}
                  </div>
                  {statusPhotos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                      {statusPhotos.map((file, i) => (
                        <div key={i} style={{ position: 'relative', width: 56, height: 56 }}>
                          <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}` }} />
                          <button onClick={() => setStatusPhotos(prev => prev.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: -4, right: -4, width: 24, height: 24, borderRadius: '50%', background: '#F44336', color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>x</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {statusSheetLinkedNotes.length > 0 && (
                    <div style={{ borderTop: `1px solid ${darkMode ? tc.borderSubtle : '#eee'}`, paddingTop: 10, marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: darkMode ? tc.textMuted : '#444', marginBottom: 6 }}>Linked Notes ({statusSheetLinkedNotes.length})</div>
                      {statusSheetLinkedNotes.map(note => {
                        const catInfo = PIN_CAT_MAP[note.category] || PIN_CAT_MAP.general;
                        return (
                          <div key={note.note_id} onClick={() => { setSelectedSegment(null); setSelectedDevice(null); setSelectedNote(note); setNoteReplies([]); loadNoteReplies(note.note_id).then(r => setNoteReplies(r || [])); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? tc.borderSubtle : '#f0f0f0'}` }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: catInfo.color, flexShrink: 0 }} />
                            <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.text}</span>
                            <span style={{ fontSize: 11, color: mutedColor, flexShrink: 0 }}>{note.status || 'open'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {hasIdentity ? (
                    <div style={{ fontSize: 12, color: darkMode ? tc.textFaint : '#999' }}>Updating as <span style={{ fontWeight: 600, color: darkMode ? tc.textMuted : '#555' }}>{commentName.trim()}</span></div>
                  ) : (
                    <div style={{ borderTop: `1px solid ${darkMode ? tc.borderSubtle : '#eee'}`, paddingTop: 10 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                        <input placeholder="Your name" value={commentName} onChange={e => { setCommentName(e.target.value); try { localStorage.setItem('lyt-commenter-name', e.target.value); } catch {} setIdentityError(false); }} style={{ flex: 1, padding: '10px 12px', fontSize: 16, borderRadius: 6, boxSizing: 'border-box', border: `1px solid ${identityError && !commentName.trim() ? '#e53935' : (darkMode ? tc.border : '#ddd')}`, outline: 'none', minHeight: 44, background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a' }} />
                        <input placeholder="Phone" value={commentPhone} onChange={e => { setCommentPhone(e.target.value); try { localStorage.setItem('lyt-commenter-phone', e.target.value); } catch {} setIdentityError(false); }} type="tel" style={{ flex: 1, padding: '10px 12px', fontSize: 16, borderRadius: 6, boxSizing: 'border-box', border: `1px solid ${identityError && !commentPhone.trim() ? '#e53935' : (darkMode ? tc.border : '#ddd')}`, outline: 'none', minHeight: 44, background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a' }} />
                      </div>
                      {identityError && <div style={{ fontSize: 12, color: tc.error, marginBottom: 6 }}>Name and phone required to update status</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Batch progress overlay */}
      {batchUpdating && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: darkMode ? tc.cardBg : '#fff',
            borderRadius: 16, padding: isMobile ? '28px 32px' : '24px 28px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            textAlign: 'center', minWidth: isMobile ? 260 : 220,
            color: textColor,
          }}>
            <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: accentColor, marginBottom: 12 }} />
            <div style={{ fontSize: isMobile ? 16 : 14, fontWeight: 700, marginBottom: 8 }}>
              Updating {batchProgress.done + 1} of {batchProgress.total}...
            </div>
            <div style={{ height: 8, borderRadius: 4, background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{
                height: '100%', borderRadius: 4,
                width: `${batchProgress.total > 0 ? Math.round(((batchProgress.done) / batchProgress.total) * 100) : 0}%`,
                background: accentColor,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ fontSize: isMobile ? 13 : 11, color: mutedColor }}>
              Please wait...
            </div>
          </div>
        </div>
      )}

      {/* Multi-select bottom action bar */}
      {multiSelectMode && multiSelectCount > 0 && !batchUpdating && (() => {
        const segCount = selectedSegments.size;
        const devCount = selectedDevices.size;
        const breakdown = [
          segCount > 0 && `${segCount} seg`,
          devCount > 0 && `${devCount} dev`,
        ].filter(Boolean).join(' + ');
        const STATUS_BUTTONS = [
          { value: 'In Progress', label: 'In Progress', color: '#FFB800' },
          { value: 'Complete', label: 'Complete', color: '#4CAF50' },
          { value: 'Issue', label: 'Issue', color: '#FF5722' },
        ];
        if (qcMode) STATUS_BUTTONS.push({ value: 'QC Approved', label: 'QC Approved', color: '#2196F3' });

        return (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
            background: darkMode ? 'rgba(30,41,59,0.95)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            borderTop: `1px solid ${borderColor}`,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
            padding: isMobile ? '12px 16px' : '10px 16px',
            paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 8, marginBottom: isMobile ? 10 : 8 }}>
              <div style={{
                fontSize: isMobile ? 14 : 12, fontWeight: 700, color: '#00e5ff',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Layers size={isMobile ? 16 : 14} />
                {breakdown}
              </div>
              <div style={{ fontSize: isMobile ? 12 : 11, color: mutedColor, flex: 1 }}>
                Photos available in single-select mode
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 6 }}>
              {STATUS_BUTTONS.map(btn => (
                <button
                  key={btn.value}
                  onClick={() => handleBatchStatusChange(btn.value)}
                  style={{
                    flex: '1 1 auto', minWidth: isMobile ? 90 : 70, minHeight: isMobile ? 48 : 36,
                    padding: isMobile ? '10px 8px' : '6px 10px', borderRadius: 8,
                    border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                    background: 'transparent',
                    color: btn.color, cursor: 'pointer',
                    fontSize: isMobile ? 14 : 12, fontWeight: 700,
                    transition: 'all 0.15s',
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
            {!hasIdentity && identityError && (
              <div style={{ fontSize: isMobile ? 12 : 11, color: tc.error, marginTop: 6 }}>
                Set your name and phone in Settings to update status
              </div>
            )}
          </div>
        );
      })()}

      {/* Pin creation form — shown after tapping to place */}
      {newPinLocation && (
        <>
          <div style={isMobile ? {
            position: 'fixed', bottom: kbHeight, left: 0, right: 0, zIndex: 1001,
            background: darkMode ? 'rgba(30,41,59,0.92)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.25)',
            paddingBottom: kbHeight > 0 ? 0 : 'env(safe-area-inset-bottom, 0px)',
            maxHeight: '60vh', overflowY: 'auto',
          } : {
            position: 'absolute', bottom: 80, right: 16, zIndex: 30,
            background: darkMode ? tc.cardBg : '#fff', borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            width: 320, border: darkMode ? `1px solid ${borderColor}` : undefined,
          }}>
            <div style={{ padding: isMobile ? '12px 16px' : 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: isMobile ? 15 : 14, fontWeight: 700, color: textColor }}>New Pin</span>
                <button onClick={cancelPinAction} style={{
                  background: 'none', border: 'none', fontSize: 22, color: mutedColor, cursor: 'pointer',
                  padding: 4, lineHeight: 1, minWidth: 44, minHeight: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>×</button>
              </div>
              <textarea
                placeholder="What needs attention here?"
                value={pinFormText}
                onChange={e => setPinFormText(e.target.value)}
                style={{
                  width: '100%', padding: '10px', fontSize: isMobile ? 16 : 14,
                  borderRadius: 8, border: `1px solid ${darkMode ? tc.border : '#ddd'}`, outline: 'none', resize: 'none',
                  minHeight: 50, boxSizing: 'border-box', fontFamily: 'inherit',
                  lineHeight: 1.4, background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#1a1a1a',
                }}
              />
              {/* Row 1: Category selector (full width on mobile) */}
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: isMobile ? '1 0 100%' : undefined }}>
                  <svg width="14" height="18" viewBox="0 0 24 32" style={{ flexShrink: 0 }}>
                    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill={(PIN_CAT_MAP[pinFormCategory] || PIN_CAT_MAP.general).color} stroke="white" strokeWidth="1.5"/>
                    <text x="12" y="15" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="sans-serif">{(PIN_CAT_MAP[pinFormCategory] || PIN_CAT_MAP.general).letter}</text>
                  </svg>
                  <select
                    value={pinFormCategory}
                    onChange={e => setPinFormCategory(e.target.value)}
                    style={{
                      padding: isMobile ? '10px 12px' : '7px 10px', fontSize: isMobile ? 14 : 12,
                      borderRadius: 8, border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                      background: darkMode ? tc.inputBg : '#fff', color: darkMode ? tc.text : '#333',
                      cursor: 'pointer', minHeight: isMobile ? 48 : undefined,
                      flex: isMobile ? 1 : undefined,
                    }}
                  >
                    {PIN_CATEGORIES.map(cat => (
                      <option key={cat.key} value={cat.key}>{cat.letter} — {cat.label}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="file"
                  ref={createPhotoRef}
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={async (e) => {
                    if (e.target.files?.length) {
                      const compressed = await Promise.all(Array.from(e.target.files).map(f => compressImage(f)));
                      setPendingPhotos(prev => [...prev, ...compressed]);
                    }
                    if (createPhotoRef.current) createPhotoRef.current.value = '';
                  }}
                  style={srOnly}
                />
                <input
                  type="file"
                  ref={createGalleryRef}
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    if (e.target.files?.length) {
                      const compressed = await Promise.all(Array.from(e.target.files).map(f => compressImage(f)));
                      setPendingPhotos(prev => [...prev, ...compressed]);
                    }
                    if (createGalleryRef.current) createGalleryRef.current.value = '';
                  }}
                  style={srOnly}
                />
                {/* Row 2: Camera + Upload (equal width on mobile) */}
                <button
                  type="button"
                  onClick={() => createPhotoRef.current?.click()}
                  style={{
                    padding: isMobile ? '10px 12px' : '7px 10px', borderRadius: 8,
                    border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                    background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555',
                    cursor: 'pointer', fontSize: isMobile ? 14 : 12,
                    minHeight: isMobile ? 48 : undefined, flex: isMobile ? '1 1 0' : undefined,
                  }}
                >📷 Camera</button>
                <button
                  type="button"
                  onClick={() => createGalleryRef.current?.click()}
                  style={{
                    padding: isMobile ? '10px 12px' : '7px 10px', borderRadius: 8,
                    border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                    background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555',
                    cursor: 'pointer', fontSize: isMobile ? 14 : 12,
                    minHeight: isMobile ? 48 : undefined, flex: isMobile ? '1 1 0' : undefined,
                  }}
                >📁 Upload</button>
                {/* Row 3: Cancel + Place Pin */}
                <div style={{ flex: '1 0 100%' }} />
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <button onClick={cancelPinAction} style={{
                    padding: isMobile ? '10px 16px' : '7px 14px', borderRadius: 8,
                    border: `1px solid ${darkMode ? tc.border : '#ddd'}`,
                    background: darkMode ? tc.cardBgAlt : '#fff', color: darkMode ? tc.textMuted : '#555',
                    cursor: 'pointer', fontSize: isMobile ? 14 : 12,
                    minHeight: isMobile ? 48 : undefined, flex: 1,
                  }}>Cancel</button>
                  <button
                    onClick={handleCreatePin}
                    disabled={pinFormSaving || !pinFormText.trim()}
                    style={{
                      padding: isMobile ? '10px 16px' : '7px 14px', borderRadius: 8, border: 'none',
                      background: pinFormSaving || !pinFormText.trim() ? (darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0') : accentColor,
                      color: pinFormSaving || !pinFormText.trim() ? mutedColor : '#fff',
                      cursor: pinFormSaving || !pinFormText.trim() ? 'default' : 'pointer',
                      fontSize: isMobile ? 14 : 12, fontWeight: 600,
                      minHeight: isMobile ? 48 : undefined, flex: 2,
                    }}
                  >{pinFormSaving ? 'Saving...' : 'Place Pin'}</button>
                </div>
              </div>
              {/* Pending photo previews */}
              {pendingPhotos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {pendingPhotos.map((file, i) => (
                    <div key={i} style={{ position: 'relative', width: isMobile ? 56 : 44, height: isMobile ? 56 : 44 }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: `1px solid ${darkMode ? tc.border : '#ddd'}` }}
                      />
                      <button
                        onClick={() => setPendingPhotos(prev => prev.filter((_, idx) => idx !== i))}
                        style={{
                          position: 'absolute', top: -4, right: -4,
                          width: 24, height: 24, borderRadius: '50%',
                          background: '#F44336', color: '#fff', border: 'none',
                          fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1, padding: 0,
                        }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: isMobile ? 12 : 11, color: mutedColor, marginTop: 8 }}>
                Placing as {commentName.trim()}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Spin animation + hide Google Maps branding */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeInDown { from { opacity: 0; transform: translateX(-50%) translateY(-8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes shimmer { 0% { opacity: 0.4; } 50% { opacity: 0.8; } 100% { opacity: 0.4; } }
        .shimmer-bar { animation: shimmer 1.5s ease-in-out infinite; }
        .gm-style-cc { display: none !important; }
        .gm-style a[title="Report errors in the road map or imagery to Google"] { display: none !important; }
        .gm-style a[title="Open this area in Google Maps (opens a new window)"] { display: none !important; }
      `}</style>
    </div>
  );
}
